<template>
  <div class="app-container" :style="dashboardCssVars">
    <!-- Mobile Menu Toggle Button -->
    <IconButton
      v-if="isMobile"
      icon="bi bi-list"
      icon-only
      custom-class="mobile-menu-toggle"
      aria-label="Open menu"
      @click="toggleMobileMenu"
    />
    
    <!-- Mobile Overlay -->
    <div class="mobile-overlay" :class="{ active: mobileMenuOpen }" @click="closeMobileMenu"></div>
    
    <!-- Top Navigation Bar -->
    <nav class="top-nav">
      <div class="nav-left">
        <a class="topnav-brand" href="#" @click.prevent="handleTopMenuClick('home')" title="AnnaBooks Home">
          <img src="@/assets/annabooks-logo.png" alt="AnnaBooks" class="topnav-brand__logo" />
        </a>
      </div>
      <div class="nav-items">
        <TopNavItem
          icon="bi bi-house"
          label="Home"
          variant="home"
          :active="activeTopMenu === 'home'"
          @click="handleTopMenuClick('home')"
        />
        <TopNavItem
          icon="bi bi-grid-3x3-gap"
          label="Dashboard"
          variant="dashboard"
          :active="activeTopMenu === 'dashboard'"
          @click="handleTopMenuClick('dashboard')"
        />
        <TopNavItem
          icon="bi bi-bar-chart"
          label="Companies"
          variant="companies"
          :active="activeTopMenu === 'companies'"
          @click="handleTopMenuClick('companies')"
        />
        <TopNavItem
          icon="bi bi-currency-dollar"
          label="Tracker"
          variant="tracker"
          :active="activeTopMenu === 'tracker'"
          @click="handleTopMenuClick('tracker')"
        />
        <TopNavItem
          icon="bi bi-file-earmark-text"
          label="Reports Home"
          variant="reports"
          :active="activeTopMenu === 'reports'"
          @click="handleTopMenuClick('reports')"
        />
        <TopNavItem
          icon="bi bi-gear"
          label="Company Preferences"
          variant="preferences"
          :active="activeTopMenu === 'preferences'"
          @click="handleTopMenuClick('preferences')"
        />
      </div>
      <div class="nav-right">
        <SessionExpiryIndicator
          class="top-nav-session"
          @logout="handleLogout"
          @session-expired="handleSessionExpired"
          @session-nearing-expiry="handleSessionNearingExpiry"
        />
      </div>
    </nav>

    <PageWindowSwitcher />

    <div class="main-content">
      <!-- Left Sidebar -->
      <aside 
        class="sidebar" 
        :class="{ collapsed: sidebarCollapsed, 'mobile-open': mobileMenuOpen, 'resizing': isResizing }"
      >
        <!-- Scrollable sidebar content -->
        <div class="sidebar-content">
          <!-- Logo removed from here -->
          <IconButton
            :icon="sidebarCollapsed ? 'bi bi-chevron-right' : 'bi bi-chevron-left'"
            icon-only
            custom-class="sidebar-toggle"
            :aria-label="sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
            @click="sidebarCollapsed = !sidebarCollapsed"
          />
        <div class="sidebar-nav-card">
        <SidebarNavItem
          v-if="industryLabel === 'Consultant'"
          icon="bi bi-person"
          label="Consultant"
          variant="consultant"
          :active="activeMenu === 'consultant'"
          :expanded="consultantMenuExpanded"
          :sidebar-collapsed="sidebarCollapsed"
          @click="toggleConsultantMenu"
        />
        <transition name="sidebar-submenu">
          <div v-if="industryLabel === 'Consultant' && consultantMenuExpanded && !sidebarCollapsed" class="customer-submenu sidebar-submenu-panel sidebar-submenu-panel--consultant">
            <SidebarSubmenuItem
              icon="bi bi-house-door"
              :active="$route.path.includes('/consultant/center')"
              @click="navigateConsultantSub('center')"
            >
              Consultant Center
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-plus-circle"
              expandable
              :expanded="consultantNewMenuExpanded"
              custom-class="customer-expandable"
              @click="toggleConsultantNewMenu"
            >
              New
            </SidebarSubmenuItem>
            <transition name="sidebar-submenu">
              <div v-if="consultantNewMenuExpanded" class="customer-submenu">
                <SidebarSubmenuItem
                  icon="bi bi-person-plus"
                  :active="$route.path.includes('/consultant/add-customer-consultant') || $route.name === 'AddCustomerConsultant'"
                  @click="navigateConsultantNew('newConsultant')"
                >
                  New Consultant
                </SidebarSubmenuItem>
              </div>
            </transition>
            <SidebarSubmenuItem
              icon="bi bi-people"
              :active="$route.path.includes('/consultant/list')"
              @click="navigateConsultantSub('list')"
            >
              Consultants
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-cash-coin"
              :active="$route.path.includes('/consultant/receive-payment')"
              @click="navigateConsultantSub('receive')"
            >
              Received Payments
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-clock-history"
              :active="$route.path.includes('/consultant/timesheet') && !$route.path.includes('timesheet-summary')"
              @click="navigateConsultantSub('timesheet')"
            >
              Consultant Time Sheet
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-bar-chart"
              :active="$route.path.includes('/consultant/timesheet-summary')"
              @click="navigateConsultantSub('timesheetSummary')"
            >
              Timesheet Summary
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-receipt"
              :active="$route.path.includes('/consultant/invoice')"
              @click="navigateConsultantSub('invoiceList')"
            >
              Consultant Invoice
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-file-earmark-text"
              expandable
              :expanded="consultantContractMenuExpanded"
              custom-class="customer-expandable"
              :active="$route.path.includes('/consultant/contract-details') || $route.path.includes('/consultant/contract-tracking')"
              @click="toggleConsultantContractMenu"
            >
              Consultant Contract
            </SidebarSubmenuItem>
            <transition name="sidebar-submenu">
              <div v-if="consultantContractMenuExpanded" class="customer-submenu">
                <SidebarSubmenuItem
                  icon="bi bi-file-earmark-text"
                  :active="$route.path.includes('/consultant/contract-details')"
                  @click="navigateConsultantSub('contractDetails')"
                >
                  Details
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-bullseye"
                  :active="$route.path.includes('/consultant/contract-tracking')"
                  @click="navigateConsultantSub('contractTracking')"
                >
                  Tracking
                </SidebarSubmenuItem>
              </div>
            </transition>
            <SidebarSubmenuItem
              icon="bi bi-heart-pulse"
              expandable
              :expanded="consultantBenefitsMenuExpanded"
              custom-class="customer-expandable"
              :active="$route.path.includes('/consultant/benefits')"
              @click="toggleConsultantBenefitsMenu"
            >
              Benefits
            </SidebarSubmenuItem>
            <transition name="sidebar-submenu">
              <div v-if="consultantBenefitsMenuExpanded" class="customer-submenu">
                <SidebarSubmenuItem
                  icon="bi bi-tags"
                  :active="$route.path.includes('/consultant/benefits/types') || $route.path.includes('/consultant/benefits/category')"
                  @click="navigateConsultantSub('benefitTypes')"
                >
                  Benefit Types
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-clipboard2-heart"
                  :active="$route.path.includes('/consultant/benefits/plans')"
                  @click="navigateConsultantSub('benefitPlans')"
                >
                  Benefit Plans
                </SidebarSubmenuItem>
              </div>
            </transition>
            <SidebarSubmenuItem
              icon="bi bi-wallet2"
              expandable
              :expanded="consultantPayrollMenuExpanded"
              custom-class="customer-expandable"
              :active="$route.path.includes('/consultant/payroll')"
              @click="toggleConsultantPayrollMenu"
            >
              Payroll Management
            </SidebarSubmenuItem>
            <transition name="sidebar-submenu">
              <div v-if="consultantPayrollMenuExpanded" class="customer-submenu">
                <SidebarSubmenuItem
                  icon="bi bi-clock-history"
                  :active="$route.path.includes('/consultant/payroll/history')"
                  @click="navigateConsultantSub('payrollHistory')"
                >
                  Payroll History
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-plus-circle"
                  :active="$route.path.includes('/consultant/payroll/record') && !$route.path.includes('/record/manual')"
                  @click="navigateConsultantSub('recordPayroll')"
                >
                  Record Payroll
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-pencil-square"
                  :active="$route.path.includes('/consultant/payroll/record/manual')"
                  @click="navigateConsultantSub('createPayroll')"
                >
                  Create Payroll
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-bookmark-star"
                  :active="$route.path.includes('/consultant/payroll/staging')"
                  @click="navigateConsultantSub('stagedPayroll')"
                >
                  Staged Payroll
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  v-if="!isInvitedUser"
                  icon="bi bi-people"
                  :active="$route.path.includes('/consultant/payroll/to-consultants')"
                  @click="navigateConsultantSub('payrollToConsultants')"
                >
                  Set payroll details
                </SidebarSubmenuItem>
              </div>
            </transition>
          </div>
        </transition>
        <SidebarNavItem
          icon="bi bi-people"
          label="Customer"
          variant="customer"
          :active="activeMenu === 'customer'"
          :expanded="customerMenuExpanded"
          :sidebar-collapsed="sidebarCollapsed"
          title="Customer"
          @click="toggleCustomerMenu"
        />
        <transition name="sidebar-submenu">
          <div v-if="customerMenuExpanded && !sidebarCollapsed" class="customer-submenu sidebar-submenu-panel sidebar-submenu-panel--customer">
            <SidebarSubmenuItem
              icon="bi bi-house-door"
              :active="$route.path.includes('/customers/center')"
              @click="navigateCustomerSub('center')"
            >
              Customer Center
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-plus-circle"
              expandable
              :expanded="customerNewMenuExpanded"
              custom-class="customer-expandable"
              @click="toggleCustomerNewMenu"
            >
              New
            </SidebarSubmenuItem>
            <transition name="sidebar-submenu">
              <div v-if="customerNewMenuExpanded" class="customer-submenu">
                <SidebarSubmenuItem
                  icon="bi bi-person-plus"
                  :active="$route.path.includes('/customers/new') && !$route.path.includes('invoice') && !$route.path.includes('cash-sale') && !$route.path.includes('credit-memo') && !$route.path.includes('service-item')"
                  @click="navigateCustomerNew('newCustomer')"
                >
                  New Customer
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-box"
                  :active="$route.path.includes('/customers/service-item') || $route.path.includes('/customers/new-service-item')"
                  @click="navigateCustomerNew('newSalesItem')"
                >
                  New Sales Item
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-file-earmark-text"
                  :active="isCustomerNewInvoiceActive"
                  @click="navigateCustomerNew('newInvoice')"
                >
                  Customer Invoice
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-file-earmark-plus"
                  :active="isCustomerCreateInvoiceActive"
                  @click="navigateCustomerNew('createInvoice')"
                >
                  Create Invoice
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-cash-stack"
                  :active="$route.path.includes('/customers/new-cash-sale')"
                  @click="navigateCustomerNew('newCashSale')"
                >
                  New Cash Sale
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-receipt-cutoff"
                  :active="$route.path.includes('/customers/new-credit-memo')"
                  @click="navigateCustomerNew('newCreditMemo')"
                >
                  New Credit Memo
                </SidebarSubmenuItem>
              </div>
            </transition>
            <SidebarSubmenuItem
              icon="bi bi-wallet2"
              :active="$route.path.includes('/customers/prepayment')"
              @click="navigateCustomerSub('prepayment')"
            >
              Customer Prepayment
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-cash-coin"
              :active="$route.path.includes('/customers/receive-payment')"
              @click="navigateCustomerSub('receive')"
            >
              Receive Payment
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-arrow-counterclockwise"
              :active="$route.path.includes('/customers/refund')"
              @click="navigateCustomerSub('refund')"
            >
              Customer Refund
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-people"
              :active="$route.path.includes('/customers/list') && !$route.path.includes('center') && !$route.path.includes('invoice')"
              @click="navigateCustomerSub('customers')"
            >
              Customers
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-receipt"
              :active="$route.path.includes('/customers/invoices')"
              @click="navigateCustomerSub('invoices')"
            >
              Invoices
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-box-seam"
              :active="$route.path.includes('/customers/items')"
              @click="navigateCustomerSub('items')"
            >
              Customers Items
            </SidebarSubmenuItem>
          </div>
        </transition>
        <SidebarNavItem
          icon="bi bi-shop"
          label="Vendor"
          variant="vendor"
          :active="activeMenu === 'vendor'"
          :expanded="vendorMenuExpanded"
          :sidebar-collapsed="sidebarCollapsed"
          @click="toggleVendorMenu"
        />
        <transition name="sidebar-submenu">
          <div v-if="vendorMenuExpanded && !sidebarCollapsed" class="customer-submenu sidebar-submenu-panel sidebar-submenu-panel--vendor">
            <SidebarSubmenuItem
              icon="bi bi-house-door"
              :active="$route.path.includes('/vendors/center')"
              @click="navigateVendorSub('center')"
            >
              Vendor Center
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-plus-circle"
              expandable
              :expanded="vendorNewMenuExpanded"
              custom-class="customer-expandable"
              @click="toggleVendorNewMenu"
            >
              New
            </SidebarSubmenuItem>
            <transition name="sidebar-submenu">
              <div v-if="vendorNewMenuExpanded" class="customer-submenu">
                <SidebarSubmenuItem
                  icon="bi bi-shop"
                  :active="$route.path.includes('/vendors/new-vendor')"
                  @click="navigateVendorSub('newvendor')"
                >
                  New Vendor
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-box-arrow-in-down"
                  :active="$route.path.includes('/vendors/service-item') || $route.path.includes('/vendors/product-item')"
                  @click="navigateVendorSub('newpurchaseitem')"
                >
                  New Purchase Item
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-cash-stack"
                  :active="$route.path.includes('/vendors/cash-purchase')"
                  @click="navigateVendorSub('cashpurchase')"
                >
                  Cash Purchase
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-credit-card"
                  :active="$route.path.includes('/vendors/vendor-credit')"
                  @click="navigateVendorSub('vendorcredit')"
                >
                  Vendor Credit
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-pencil-square"
                  :active="$route.path.includes('/vendors/write-check')"
                  @click="navigateVendorSub('writecheck')"
                >
                  Write check
                </SidebarSubmenuItem>
              </div>
            </transition>
            <SidebarSubmenuItem
              icon="bi bi-printer"
              :active="$route.path.includes('/vendors/print-check')"
              @click="navigateVendorSub('printcheck')"
            >
              Print Check
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-receipt-cutoff"
              expandable
              :expanded="vendorBillsMenuExpanded"
              custom-class="customer-expandable"
              @click="toggleVendorBillsMenu"
            >
              Bills
            </SidebarSubmenuItem>
            <transition name="sidebar-submenu">
              <div v-if="vendorBillsMenuExpanded" class="customer-submenu">
                <SidebarSubmenuItem
                  icon="bi bi-file-earmark-text"
                  :active="$route.path.includes('/vendors/enter-bill')"
                  @click="navigateVendorSub('enterbill')"
                >
                  Vendor Bill
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  v-if="billTracking"
                  icon="bi bi-file-earmark-plus"
                  :active="isVendorCreateBillActive"
                  @click="navigateVendorSub('createbill')"
                >
                  Create Bill
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  v-if="billTracking"
                  icon="bi bi-file-earmark-diff"
                  :active="isVendorDraftBillsActive"
                  @click.stop="navigateVendorSub('draftbills')"
                >
                  Drafts & Captured
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  v-if="billTracking"
                  icon="bi bi-envelope-check"
                  :active="isVendorMailSourcesActive"
                  @click.stop="navigateVendorSub('mailsources')"
                >
                  Bill Mail Setup
                </SidebarSubmenuItem>
              </div>
            </transition>
            <SidebarSubmenuItem
              icon="bi bi-cash"
              expandable
              :expanded="vendorPayBillsMenuExpanded"
              custom-class="customer-expandable"
              @click="toggleVendorPayBillsMenu"
            >
              Pay Bills
            </SidebarSubmenuItem>
            <transition name="sidebar-submenu">
              <div v-if="vendorPayBillsMenuExpanded" class="customer-submenu">
                <SidebarSubmenuItem
                  v-if="billTracking"
                  icon="bi bi-cash"
                  :active="isVendorPayBillActive"
                  @click="navigateVendorSub('paybill')"
                >
                  Pay Bill
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-wallet2"
                  :active="$route.path.includes('/vendors/payments')"
                  @click="navigateVendorSub('payments')"
                >
                  Vendor Payments
                </SidebarSubmenuItem>
              </div>
            </transition>
            <SidebarSubmenuItem
              icon="bi bi-receipt"
              :active="$route.path.includes('/vendors/bills')"
              @click="navigateVendorSub('bills')"
            >
              Bills And Expenses
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-journal-text"
              :active="$route.path.includes('/vendors/record-expenses')"
              @click="navigateVendorSub('expenses')"
            >
              Record Expenses
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-shop"
              :active="$route.path.includes('/vendors/list')"
              @click="navigateVendorSub('vendors')"
            >
              Vendors
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              v-if="isCompanyOwner"
              icon="bi bi-box-arrow-in-right"
              :active="$route.path.includes('/vendors/portal-hub')"
              @click="navigateVendorSub('portalhub')"
            >
              Invites & Submitted
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-box-seam"
              :active="$route.path.includes('/vendors/items')"
              @click="navigateVendorSub('items')"
            >
              Vendors Items
            </SidebarSubmenuItem>
          </div>
        </transition>
        <SidebarNavItem
          icon="bi bi-box-seam"
          label="Inventory"
          variant="inventory"
          :active="activeMenu === 'inventory'"
          :expanded="inventoryMenuExpanded"
          :sidebar-collapsed="sidebarCollapsed"
          @click="toggleInventoryMenu"
        />
        <transition name="sidebar-submenu">
          <div v-if="inventoryMenuExpanded && !sidebarCollapsed" class="customer-submenu sidebar-submenu-panel sidebar-submenu-panel--inventory">
            <SidebarSubmenuItem
              icon="bi bi-house-door"
              :active="$route.path.includes('/inventory/center') || $route.path.includes('/inventory/centre')"
              @click="navigateInventorySub('centre')"
            >
              Inventory Centre
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-tools"
              :active="$route.path.includes('/inventory/build-assembly')"
              @click="navigateInventorySub('buildassembly')"
            >
              Build Assembly
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-plus-circle"
              expandable
              :expanded="inventoryNewMenuExpanded"
              custom-class="customer-expandable"
              @click="toggleInventoryNewMenu"
            >
              New
            </SidebarSubmenuItem>
            <transition name="sidebar-submenu">
              <div v-if="inventoryNewMenuExpanded" class="customer-submenu">
                <SidebarSubmenuItem
                  icon="bi bi-arrow-left-right"
                  :active="$route.path.includes('/inventory/stock-adjustment/new')"
                  @click="navigateInventorySub('stockadjustment')"
                >
                  Stock Adjustment
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-box"
                  :active="$route.path.includes('/inventory/new-item') || $route.path.includes('/dashboard/new-inventory-item')"
                  @click="navigateInventorySub('newitem')"
                >
                  New Inventory Item
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-boxes"
                  :active="$route.path.includes('/inventory/new-assembly') || $route.path.includes('/dashboard/new-inventory-assembly')"
                  @click="navigateInventorySub('newassembly')"
                >
                  New Inventory Assembly
                </SidebarSubmenuItem>
              </div>
            </transition>
            <SidebarSubmenuItem
              icon="bi bi-box-seam"
              :active="$route.path.includes('/inventory/items')"
              @click="navigateInventorySub('items')"
            >
              Inventory items
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-arrow-left-right"
              :active="$route.path.includes('/inventory/stock-adjustment') && !$route.path.includes('new')"
              @click="navigateInventorySub('adjustments')"
            >
              Stock Adjustments
            </SidebarSubmenuItem>
          </div>
        </transition>
        <SidebarNavItem
          icon="bi bi-bank"
          label="Banking"
          variant="banking"
          :active="activeMenu === 'banking'"
          :expanded="bankingMenuExpanded"
          :sidebar-collapsed="sidebarCollapsed"
          @click="toggleBankingMenu"
        />
        <transition name="sidebar-submenu">
          <div v-if="bankingMenuExpanded && !sidebarCollapsed" class="customer-submenu sidebar-submenu-panel sidebar-submenu-panel--banking">
            <SidebarSubmenuItem
              icon="bi bi-bank"
              :active="$route.path.includes('/banking/new-account')"
              @click="navigateBankingSub('newaccount')"
            >
              New Bank Account
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-pencil-square"
              :active="$route.path.includes('/banking/write-check')"
              @click="navigateBankingSub('writecheck')"
            >
              Write check
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-arrow-left-right"
              :active="$route.path.includes('/banking/transfer-fund')"
              @click="navigateBankingSub('transferfund')"
            >
              Transfer Fund
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-credit-card"
              :active="$route.path.includes('/banking/pay-credit-card')"
              @click="navigateBankingSub('paycreditcard')"
            >
              Pay Credit Card
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-credit-card-2-front"
              expandable
              :expanded="bankingCreditCardsMenuExpanded"
              custom-class="customer-expandable"
              @click="toggleBankingCreditCardsMenu"
            >
              Credit Cards
            </SidebarSubmenuItem>
            <transition name="sidebar-submenu">
              <div v-if="bankingCreditCardsMenuExpanded" class="customer-submenu">
                <SidebarSubmenuItem
                  icon="bi bi-plus-circle"
                  :active="$route.path.includes('/banking/credit-cards/add')"
                  @click="navigateBankingSub('addcreditcard')"
                >
                  Add Credit Card
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-card-list"
                  :active="$route.path.includes('/banking/credit-cards/details') || ($route.path.includes('/banking/credit-cards') && !$route.path.includes('/add'))"
                  @click="navigateBankingSub('creditcardsdetails')"
                >
                  Credit Cards Details
                </SidebarSubmenuItem>
              </div>
            </transition>
            <SidebarSubmenuItem
              icon="bi bi-cash-stack"
              expandable
              :expanded="bankingLoansMenuExpanded"
              custom-class="customer-expandable"
              @click="toggleBankingLoansMenu"
            >
              Loans
            </SidebarSubmenuItem>
            <transition name="sidebar-submenu">
              <div v-if="bankingLoansMenuExpanded" class="customer-submenu">
                <SidebarSubmenuItem
                  icon="bi bi-plus-circle"
                  :active="$route.path.includes('/banking/loans/add')"
                  @click="navigateBankingSub('addloan')"
                >
                  Add Loan
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-card-list"
                  :active="$route.path.includes('/banking/loans/details') || ($route.path.includes('/banking/loans') && !$route.path.includes('/organization-loans') && !$route.path.includes('/add') && !$route.path.includes('/record-payment') && !$route.path.includes('/lenders'))"
                  @click="navigateBankingSub('loansdetails')"
                >
                  Loans Details
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-cash-coin"
                  :active="$route.path.includes('/banking/loans/record-payment')"
                  @click="navigateBankingSub('recordloanpayment')"
                >
                  Record Loan Payment
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-bank"
                  expandable
                  :expanded="bankingLoansLendersMenuExpanded"
                  custom-class="customer-expandable"
                  @click="toggleBankingLoansLendersMenu"
                >
                  Lenders
                </SidebarSubmenuItem>
                <transition name="sidebar-submenu">
                  <div v-if="bankingLoansLendersMenuExpanded" class="customer-submenu">
                    <SidebarSubmenuItem
                      icon="bi bi-plus-circle"
                      :active="$route.path.includes('/banking/loans/lenders/add')"
                      @click="navigateBankingSub('addloanlender')"
                    >
                      Add Lender
                    </SidebarSubmenuItem>
                    <SidebarSubmenuItem
                      icon="bi bi-card-list"
                      :active="$route.path.includes('/banking/loans/lenders/details') || ($route.path.includes('/banking/loans/lenders') && !$route.path.includes('/add'))"
                      @click="navigateBankingSub('loanlendersdetails')"
                    >
                      Lender Details
                    </SidebarSubmenuItem>
                  </div>
                </transition>
              </div>
            </transition>
            <SidebarSubmenuItem
              icon="bi bi-building"
              expandable
              :expanded="bankingOrgLoansMenuExpanded"
              custom-class="customer-expandable"
              @click="toggleBankingOrgLoansMenu"
            >
              Organization Loans
            </SidebarSubmenuItem>
            <transition name="sidebar-submenu">
              <div v-if="bankingOrgLoansMenuExpanded" class="customer-submenu">
                <SidebarSubmenuItem
                  icon="bi bi-plus-circle"
                  :active="$route.path.includes('/banking/organization-loans/add')"
                  @click="navigateBankingSub('addorgloan')"
                >
                  Add Loan
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-card-list"
                  :active="$route.path.includes('/banking/organization-loans/details') || ($route.path.includes('/banking/organization-loans') && !$route.path.includes('/add') && !$route.path.includes('/record-payment') && !$route.path.includes('/record-collection') && !$route.path.includes('/organizations'))"
                  @click="navigateBankingSub('orgloansdetails')"
                >
                  Loans Details
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-cash-coin"
                  :active="$route.path.includes('/banking/organization-loans/record-payment')"
                  @click="navigateBankingSub('recordorgloanpayment')"
                >
                  Org Loan Payment
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-cash-stack"
                  :active="$route.path.includes('/banking/organization-loans/record-collection')"
                  @click="navigateBankingSub('recordorgloancollection')"
                >
                  Org Collect Loan
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-people"
                  expandable
                  :expanded="bankingOrgLoansOrgsMenuExpanded"
                  custom-class="customer-expandable"
                  @click="toggleBankingOrgLoansOrgsMenu"
                >
                  Organizations
                </SidebarSubmenuItem>
                <transition name="sidebar-submenu">
                  <div v-if="bankingOrgLoansOrgsMenuExpanded" class="customer-submenu">
                    <SidebarSubmenuItem
                      icon="bi bi-plus-circle"
                      :active="$route.path.includes('/banking/organization-loans/organizations/add')"
                      @click="navigateBankingSub('addorgloanlender')"
                    >
                      Add Organization
                    </SidebarSubmenuItem>
                    <SidebarSubmenuItem
                      icon="bi bi-card-list"
                      :active="$route.path.includes('/banking/organization-loans/organizations/details') || ($route.path.includes('/banking/organization-loans/organizations') && !$route.path.includes('/add'))"
                      @click="navigateBankingSub('orgloanlendersdetails')"
                    >
                      Organization Details
                    </SidebarSubmenuItem>
                  </div>
                </transition>
              </div>
            </transition>
            <SidebarSubmenuItem
              icon="bi bi-arrow-down-circle"
              :active="$route.path.includes('/banking/make-deposit')"
              @click="navigateBankingSub('makedeposit')"
            >
              Make Deposit
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-clock-history"
              :active="$route.path.includes('/banking/reconciliations-history')"
              @click="navigateBankingSub('reconhistory')"
            >
              Reconciliations History
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-bank2"
              :active="$route.path.includes('/banking/accounts')"
              @click="navigateBankingSub('accounts')"
            >
              Bank Accounts
            </SidebarSubmenuItem>
          </div>
        </transition>
        <SidebarNavItem
          icon="bi bi-building"
          label="Company"
          variant="company"
          :active="activeMenu === 'company'"
          :expanded="companyMenuExpanded"
          :sidebar-collapsed="sidebarCollapsed"
          @click="toggleCompanyMenu"
        />
        <transition name="sidebar-submenu">
          <div v-if="companyMenuExpanded && !sidebarCollapsed" class="customer-submenu sidebar-submenu-panel sidebar-submenu-panel--company">
            <SidebarSubmenuItem
              icon="bi bi-grid-3x3-gap"
              :active="$route.name === 'CompanyDashboard'"
              @click="navigateCompanySub('dashboard')"
            >
              Dashboard
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-file-earmark-arrow-up"
              :active="$route.path.includes('/dashboard/importcsv')"
              @click="navigateCompanySub('importcsv')"
            >
              Import From File
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-clipboard2-check"
              :active="$route.path.includes('/dashboard/import-staging') || $route.path.includes('/dashboard/receipt-import-staging') || $route.path.includes('/dashboard/credit-card-import-staging')"
              @click="navigateCompanySub('import-staging')"
            >
              Review Staged Imports
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-journal-text"
              :active="$route.path.includes('/dashboard/journalentry')"
              @click="navigateCompanySub('journalentry')"
            >
              Journal Entry
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-arrow-left-right"
              :active="$route.path.includes('/dashboard/transactions')"
              @click="navigateCompanySub('transactionscenter')"
            >
              Transactions Center
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-plus-circle"
              :active="$route.path.includes('/dashboard/new-account') || $route.path.includes('/company/add-account')"
              @click="navigateCompanySub('newaccount')"
            >
              New Account
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-gear"
              :active="$route.path.includes('/dashboard/company-preferences') || $route.path.includes('/company-preferences')"
              @click="navigateCompanySub('preferences')"
            >
              Company Preferences
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-pie-chart"
              :active="$route.path.includes('/dashboard/company/budget')"
              @click="navigateCompanySub('budget')"
            >
              Budget
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-list-check"
              expandable
              :expanded="companySupportListsExpanded"
              custom-class="customer-expandable"
              @click="toggleCompanySupportLists"
            >
              Manage Support Lists
            </SidebarSubmenuItem>
            <transition name="sidebar-submenu">
              <div v-if="companySupportListsExpanded" class="customer-submenu">
                <SidebarSubmenuItem
                  icon="bi bi-people"
                  :active="$route.path.includes('/support-lists/customer-group')"
                  @click="navigateCompanySupportList('customer')"
                >
                  Customer Group List
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-shop"
                  :active="$route.path.includes('/support-lists/vendor-group')"
                  @click="navigateCompanySupportList('vendor')"
                >
                  Vendor Group List
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-arrow-repeat"
                  :active="$route.path.includes('/support-lists/invoice-frequency')"
                  @click="navigateCompanySupportList('invoice-frequency')"
                >
                  Invoice Frequency List
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-calendar-week"
                  :active="$route.path.includes('/support-lists/timesheet-frequency')"
                  @click="navigateCompanySupportList('timesheet-frequency')"
                >
                  Timesheet Frequency List
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-clock"
                  :active="$route.path.includes('/support-lists/payment-term')"
                  @click="navigateCompanySupportList('payment')"
                >
                  Payment Term List
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-boxes"
                  :active="$route.path.includes('/support-lists/item-group')"
                  @click="navigateCompanySupportList('item')"
                >
                  Item Group List
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-building"
                  :active="$route.path.includes('/support-lists/warehouses')"
                  @click="navigateCompanySupportList('warehouses')"
                >
                  Warehouses List
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-truck"
                  :active="$route.path.includes('/support-lists/shipping-method')"
                  @click="navigateCompanySupportList('shipping')"
                >
                  Preferred Shipping Method List
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-journal-text"
                  expandable
                  :expanded="companyNotesListsExpanded"
                  custom-class="customer-expandable"
                  :active="$route.path.includes('/support-lists/notes-list')
                    || $route.path.includes('/support-lists/vendor-notes-list')
                    || $route.path.includes('/support-lists/note-tags')
                    || $route.path.includes('/support-lists/note-types')
                    || $route.path.includes('/support-lists/note-priorities')"
                  @click="toggleCompanyNotesLists"
                >
                  Notes
                </SidebarSubmenuItem>
                <transition name="sidebar-submenu">
                  <div v-if="companyNotesListsExpanded" class="customer-submenu">
                    <SidebarSubmenuItem
                      icon="bi bi-journal-bookmark"
                      :active="$route.path.includes('/support-lists/notes-list')"
                      @click="navigateCompanySupportList('notes-list')"
                    >
                      Customer Notes List
                    </SidebarSubmenuItem>
                    <SidebarSubmenuItem
                      icon="bi bi-truck"
                      :active="$route.path.includes('/support-lists/vendor-notes-list')"
                      @click="navigateCompanySupportList('vendor-notes-list')"
                    >
                      Vendor Notes List
                    </SidebarSubmenuItem>
                    <SidebarSubmenuItem
                      icon="bi bi-tags"
                      :active="$route.path.includes('/support-lists/note-tags')"
                      @click="navigateCompanySupportList('note-tags')"
                    >
                      Note Tag List
                    </SidebarSubmenuItem>
                    <SidebarSubmenuItem
                      icon="bi bi-card-list"
                      :active="$route.path.includes('/support-lists/note-types')"
                      @click="navigateCompanySupportList('note-types')"
                    >
                      Note Type List
                    </SidebarSubmenuItem>
                    <SidebarSubmenuItem
                      icon="bi bi-flag"
                      :active="$route.path.includes('/support-lists/note-priorities')"
                      @click="navigateCompanySupportList('note-priorities')"
                    >
                      Note Priority List
                    </SidebarSubmenuItem>
                  </div>
                </transition>
              </div>
            </transition>
            <SidebarSubmenuItem
              icon="bi bi-hdd-stack"
              expandable
              :expanded="companyFixedAssetsExpanded"
              custom-class="customer-expandable"
              @click="toggleCompanyFixedAssets"
            >
              Fixed Assets
            </SidebarSubmenuItem>
            <transition name="sidebar-submenu">
              <div v-if="companyFixedAssetsExpanded" class="customer-submenu">
                <SidebarSubmenuItem
                  icon="bi bi-plus-circle"
                  :active="$route.path.includes('/fixed-assets/new')"
                  @click="navigateCompanyFixedAssets('new')"
                >
                  New Fixed Asset
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-graph-down"
                  :active="$route.path.includes('/fixed-assets/depreciation')"
                  @click="navigateCompanyFixedAssets('depreciation')"
                >
                  Depreciation
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-hourglass-split"
                  :active="$route.path.includes('/fixed-assets/pending-items')"
                  @click="navigateCompanyFixedAssets('pending')"
                >
                  Pending Items List
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-check-circle"
                  :active="$route.path.includes('/fixed-assets/registered-items')"
                  @click="navigateCompanyFixedAssets('registered')"
                >
                  Registered Items List
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-x-circle"
                  :active="$route.path.includes('/fixed-assets/sold')"
                  @click="navigateCompanyFixedAssets('sold')"
                >
                  Sold & Disposed Items
                </SidebarSubmenuItem>
              </div>
            </transition>
            <SidebarSubmenuItem
              icon="bi bi-arrow-left-right"
              expandable
              :expanded="companyMergeAccountsExpanded"
              custom-class="customer-expandable"
              @click="toggleCompanyMergeAccounts"
            >
              Merge Accounts
            </SidebarSubmenuItem>
            <transition name="sidebar-submenu">
              <div v-if="companyMergeAccountsExpanded" class="customer-submenu">
                <SidebarSubmenuItem
                  icon="bi bi-people"
                  :active="$route.path.includes('/merge-accounts/customers')"
                  @click="navigateCompanyMergeAccounts('customers')"
                >
                  Merge Customers
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-shop"
                  :active="$route.path.includes('/merge-accounts/vendors')"
                  @click="navigateCompanyMergeAccounts('vendors')"
                >
                  Merge Vendors
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-wallet2"
                  :active="$route.path.includes('/merge-accounts/accounts')"
                  @click="navigateCompanyMergeAccounts('accounts')"
                >
                  Merge Accounts
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-box-seam"
                  :active="$route.path.includes('/merge-accounts/items')"
                  @click="navigateCompanyMergeAccounts('items')"
                >
                  Merge Items
                </SidebarSubmenuItem>
              </div>
            </transition>
            <SidebarSubmenuItem
              icon="bi bi-list-ul"
              expandable
              :expanded="companyListsExpanded"
              custom-class="customer-expandable"
              @click="toggleCompanyLists"
            >
              Company Lists
            </SidebarSubmenuItem>
            <transition name="sidebar-submenu">
              <div v-if="companyListsExpanded" class="customer-submenu">
                <SidebarSubmenuItem
                  icon="bi bi-wallet2"
                  :active="$route.path.includes('/company-account-list') || $route.path.includes('/company/lists/accounts')"
                  @click="navigateCompanyLists('accounts')"
                >
                  Accounts List
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-box-seam"
                  :active="$route.path.includes('/company/lists/items')"
                  @click="navigateCompanyLists('items')"
                >
                  Items
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-people"
                  :active="$route.path.includes('/customers/list') && !$route.path.includes('center')"
                  @click="navigateCompanyLists('customers')"
                >
                  Customers
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-shop"
                  :active="$route.path.includes('/vendors/list')"
                  @click="navigateCompanyLists('vendors')"
                >
                  Vendors
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-person"
                  :active="$route.path.includes('/consultant/list')"
                  @click="navigateCompanyLists('consultants')"
                >
                  Consultants
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-person-badge"
                  :active="$route.path.includes('/company/lists/salespersons')"
                  @click="navigateCompanyLists('salespersons')"
                >
                  Sales Persons
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-activity"
                  :active="$route.path.includes('/company/lists/activitylog')"
                  @click="navigateCompanyLists('activitylog')"
                >
                  Users Activity Log
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-arrow-repeat"
                  :active="$route.path.includes('/company/lists/recurring')"
                  @click="navigateCompanyLists('recurring')"
                >
                  Recurring Transactions
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-bell"
                  :active="$route.path.includes('/company/lists/reminders')"
                  @click="navigateCompanyLists('reminders')"
                >
                  Reminders List
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-currency-dollar"
                  :active="$route.path.includes('/company/lists/currency')"
                  @click="navigateCompanyLists('currency')"
                >
                  Manage Currency
                </SidebarSubmenuItem>
              </div>
            </transition>
          </div>
        </transition>
        <SidebarNavItem
          icon="bi bi-file-text"
          label="Reports"
          variant="reports"
          :active="activeMenu === 'reports'"
          :expanded="reportsMenuExpanded"
          :sidebar-collapsed="sidebarCollapsed"
          @click="toggleReportsMenu"
        />
        <transition name="sidebar-submenu">
          <div v-if="reportsMenuExpanded && !sidebarCollapsed" class="customer-submenu sidebar-submenu-panel sidebar-submenu-panel--reports">
            <SidebarSubmenuItem
              icon="bi bi-house-door"
              :active="$route.path.includes('/reports/home')"
              @click="navigateReportsSub('home')"
            >
              Reports Home
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-building"
              expandable
              :expanded="reportsCompanyFinanceExpanded"
              custom-class="customer-expandable"
              @click="toggleReportsCompanyFinance"
            >
              Company And Finance
            </SidebarSubmenuItem>
            <transition name="sidebar-submenu">
              <div v-if="reportsCompanyFinanceExpanded" class="customer-submenu">
                <SidebarSubmenuItem
                  icon="bi bi-graph-up"
                  :active="$route.path.includes('/reports/company-finance/profit-loss')"
                  @click="navigateReportsCompanyFinance('profitloss')"
                >
                  Profit And Loss
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-file-earmark-spreadsheet"
                  :active="$route.path.includes('/reports/company-finance/balance-sheet')"
                  @click="navigateReportsCompanyFinance('balancesheet')"
                >
                  Balance Sheet
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-arrow-left-right"
                  :active="$route.path.includes('/reports/company-finance/cash-flow')"
                  @click="navigateReportsCompanyFinance('cashflow')"
                >
                  Cash Flow Report
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-calculator"
                  :active="$route.path.includes('/reports/company-finance/trial-balance')"
                  @click="navigateReportsCompanyFinance('trialbalance')"
                >
                  Trial Balance
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-list-ul"
                  :active="$route.path.includes('/reports/company-finance/transaction-detail')"
                  @click="navigateReportsCompanyFinance('transactiondetail')"
                >
                  Transaction Detail by Account
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-people"
                  :active="$route.path.includes('/reports/company-finance/income-by-customer')"
                  @click="navigateReportsCompanyFinance('incomebycustomer')"
                >
                  Income by Customer Detail
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-journal-text"
                  :active="$route.path.includes('/reports/company-finance/general-ledger')"
                  @click="navigateReportsCompanyFinance('generalledger')"
                >
                  General Ledger Report
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-cash-stack"
                  :active="$route.path.includes('/reports/company-finance/expense')"
                  @click="navigateReportsCompanyFinance('expense')"
                >
                  Expense Report
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-arrow-repeat"
                  :active="$route.path.includes('/reports/company-finance/automatic-transactions')"
                  @click="navigateReportsCompanyFinance('automatictransactions')"
                >
                  Automatic Transactions
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-check-circle"
                  :active="$route.path.includes('/reports/company-finance/reconciliation')"
                  @click="navigateReportsCompanyFinance('reconciliation')"
                >
                  Reconciliation Reports
                </SidebarSubmenuItem>
              </div>
            </transition>
            <SidebarSubmenuItem
              icon="bi bi-people"
              expandable
              :expanded="reportsCustomersReceivableExpanded"
              custom-class="customer-expandable"
              @click="toggleReportsCustomersReceivable"
            >
              Customers and Receivable
            </SidebarSubmenuItem>
            <transition name="sidebar-submenu">
              <div v-if="reportsCustomersReceivableExpanded" class="customer-submenu">
                <SidebarSubmenuItem
                  icon="bi bi-clock-history"
                  :active="$route.path.includes('/reports/customers-receivable/ar-aging-summary')"
                  @click="navigateReportsCustomersReceivable('agingSummary')"
                >
                  A/R Aging Summary
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-list-check"
                  :active="$route.path.includes('/reports/customers-receivable/ar-aging-detail')"
                  @click="navigateReportsCustomersReceivable('agingDetail')"
                >
                  A/R Aging Detail
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-file-text"
                  :active="$route.path.includes('/reports/customers-receivable/client-statement')"
                  @click="navigateReportsCustomersReceivable('statement')"
                >
                  Customers Statement
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-clock-history"
                  :active="$route.path.includes('/reports/customers-receivable/customer-transaction-history')"
                  @click="navigateReportsCustomersReceivable('history')"
                >
                  Customer Transaction History
                </SidebarSubmenuItem>
              </div>
            </transition>
            <SidebarSubmenuItem
              icon="bi bi-cart"
              expandable
              :expanded="reportsSalesExpanded"
              custom-class="customer-expandable"
              @click="toggleReportsSales"
            >
              Sales
            </SidebarSubmenuItem>
            <transition name="sidebar-submenu">
              <div v-if="reportsSalesExpanded" class="customer-submenu">
                <SidebarSubmenuItem
                  icon="bi bi-bar-chart"
                  :active="$route.path.includes('/reports/sales/customer-summary')"
                  @click="navigateReportsSales('customerSummary')"
                >
                  Sales by Customer Summary
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-list-ul"
                  :active="$route.path.includes('/reports/sales/customer-detail')"
                  @click="navigateReportsSales('customerDetail')"
                >
                  Sales by Customer Detail
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-bar-chart"
                  :active="$route.path.includes('/reports/sales/item-summary')"
                  @click="navigateReportsSales('itemSummary')"
                >
                  Sales by Consultant Summary
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-list-ul"
                  :active="$route.path.includes('/reports/sales/item-detail')"
                  @click="navigateReportsSales('itemDetail')"
                >
                  Sales by Consultant Detail
                </SidebarSubmenuItem>
              </div>
            </transition>
            <SidebarSubmenuItem
              icon="bi bi-shop"
              expandable
              :expanded="reportsVendorsPayablesExpanded"
              custom-class="customer-expandable"
              @click="toggleReportsVendorsPayables"
            >
              Vendors and payables
            </SidebarSubmenuItem>
            <transition name="sidebar-submenu">
              <div v-if="reportsVendorsPayablesExpanded" class="customer-submenu">
                <SidebarSubmenuItem
                  icon="bi bi-clock-history"
                  :active="$route.path.includes('/reports/vendors-payables/ap-aging-summary')"
                  @click="navigateReportsVendorsPayables('agingSummary')"
                >
                  A/P Aging Summary
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-list-check"
                  :active="$route.path.includes('/reports/vendors-payables/ap-aging-detail')"
                  @click="navigateReportsVendorsPayables('agingDetail')"
                >
                  A/P Aging Detail
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-file-text"
                  :active="$route.path.includes('/reports/vendors-payables/vendors-statement')"
                  @click="navigateReportsVendorsPayables('statement')"
                >
                  Vendors Statement
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-clock-history"
                  :active="$route.path.includes('/reports/vendors-payables/vendor-transaction-history')"
                  @click="navigateReportsVendorsPayables('history')"
                >
                  Vendor Transaction History
                </SidebarSubmenuItem>
              </div>
            </transition>
            <SidebarSubmenuItem
              icon="bi bi-cart-plus"
              expandable
              :expanded="reportsPurchaseExpanded"
              custom-class="customer-expandable"
              @click="toggleReportsPurchase"
            >
              Purchase
            </SidebarSubmenuItem>
            <transition name="sidebar-submenu">
              <div v-if="reportsPurchaseExpanded" class="customer-submenu">
                <SidebarSubmenuItem
                  icon="bi bi-bar-chart"
                  :active="$route.path.includes('/reports/purchase/purchase-by-vendor-summary')"
                  @click="navigateReportsPurchase('vendorSummary')"
                >
                  Purchase by Vendor Summary
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-list-ul"
                  :active="$route.path.includes('/reports/purchase/purchase-by-vendor-detail')"
                  @click="navigateReportsPurchase('vendorDetail')"
                >
                  Purchase by Vendor Detail
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-bar-chart"
                  :active="$route.path.includes('/reports/purchase/purchase-by-item-summary')"
                  @click="navigateReportsPurchase('itemSummary')"
                >
                  Purchase by Item Summary
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-list-ul"
                  :active="$route.path.includes('/reports/purchase/purchase-by-item-detail')"
                  @click="navigateReportsPurchase('itemDetail')"
                >
                  Purchase by Item Detail
                </SidebarSubmenuItem>
              </div>
            </transition>
            <SidebarSubmenuItem
              icon="bi bi-pie-chart"
              expandable
              :expanded="reportsBudgetExpanded"
              custom-class="customer-expandable"
              @click="toggleReportsBudget"
            >
              Budget
            </SidebarSubmenuItem>
            <transition name="sidebar-submenu">
              <div v-if="reportsBudgetExpanded" class="customer-submenu">
                <SidebarSubmenuItem
                  icon="bi bi-eye"
                  :active="$route.path.includes('/reports/budget/overview')"
                  @click="navigateReportsBudget('overview')"
                >
                  Budget Overview
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-graph-up-arrow"
                  :active="$route.path.includes('/reports/budget/vs-actuals')"
                  @click="navigateReportsBudget('vsactuals')"
                >
                  Budget vs Actuals
                </SidebarSubmenuItem>
              </div>
            </transition>
            <SidebarSubmenuItem
              icon="bi bi-hdd-stack"
              expandable
              :expanded="reportsFixedAssetExpanded"
              custom-class="customer-expandable"
              @click="toggleReportsFixedAsset"
            >
              Fixed Asset
            </SidebarSubmenuItem>
            <transition name="sidebar-submenu">
              <div v-if="reportsFixedAssetExpanded" class="customer-submenu">
                <SidebarSubmenuItem
                  icon="bi bi-graph-down"
                  :active="$route.path.includes('/reports/fixed-asset/depreciation')"
                  @click="navigateReportsFixedAsset('depreciation')"
                >
                  Depreciation Report
                </SidebarSubmenuItem>
              </div>
            </transition>
            <SidebarSubmenuItem
              icon="bi bi-box-seam"
              expandable
              :expanded="reportsInventoryExpanded"
              custom-class="customer-expandable"
              @click="toggleReportsInventory"
            >
              Inventory
            </SidebarSubmenuItem>
            <transition name="sidebar-submenu">
              <div v-if="reportsInventoryExpanded" class="customer-submenu">
                <SidebarSubmenuItem
                  icon="bi bi-bar-chart"
                  :active="$route.path.includes('/reports/inventory/valuation-summary')"
                  @click="navigateReportsInventory('valuationSummary')"
                >
                  Inventory Valuation Summary
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-list-ul"
                  :active="$route.path.includes('/reports/inventory/valuation-details')"
                  @click="navigateReportsInventory('valuationDetails')"
                >
                  Inventory Valuation Details
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-box"
                  :active="$route.path.includes('/reports/inventory/stock-status-by-item')"
                  @click="navigateReportsInventory('stockStatusByItem')"
                >
                  Inventory Stock Status By Item
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-list-check"
                  :active="$route.path.includes('/reports/inventory/details')"
                  @click="navigateReportsInventory('details')"
                >
                  Inventory Details
                </SidebarSubmenuItem>
              </div>
            </transition>
            <SidebarSubmenuItem
              icon="bi bi-bank"
              expandable
              :expanded="reportsBankingExpanded"
              custom-class="customer-expandable"
              @click="toggleReportsBanking"
            >
              Banking
            </SidebarSubmenuItem>
            <transition name="sidebar-submenu">
              <div v-if="reportsBankingExpanded" class="customer-submenu">
                <SidebarSubmenuItem
                  icon="bi bi-exclamation-triangle"
                  :active="$route.path.includes('/reports/banking/missing-checks')"
                  @click="navigateReportsBanking('missingChecks')"
                >
                  Missing Checks
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-x-circle"
                  :active="$route.path.includes('/reports/banking/reconciliation-discrepancy')"
                  @click="navigateReportsBanking('reconciliationDiscrepancy')"
                >
                  Reconciliation Discrepancy
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-arrow-down-circle"
                  :active="$route.path.includes('/reports/banking/deposit-detail')"
                  @click="navigateReportsBanking('depositDetail')"
                >
                  Deposit Detail
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-pencil-square"
                  :active="$route.path.includes('/reports/banking/check-detail')"
                  @click="navigateReportsBanking('checkDetail')"
                >
                  Check Detail
                </SidebarSubmenuItem>
              </div>
            </transition>
            <SidebarSubmenuItem
              icon="bi bi-wallet2"
              expandable
              :expanded="reportsPayrollExpanded"
              custom-class="customer-expandable"
              @click="toggleReportsPayroll"
            >
              Payroll
            </SidebarSubmenuItem>
            <transition name="sidebar-submenu">
              <div v-if="reportsPayrollExpanded" class="customer-submenu">
                <SidebarSubmenuItem
                  icon="bi bi-bar-chart"
                  :active="$route.path.includes('/reports/payroll/pay-slip-summary')"
                  @click="navigateReportsPayroll('paySlipSummary')"
                >
                  Pay Slip Summary
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-list-ul"
                  :active="$route.path.includes('/reports/payroll/pay-slip-detail')"
                  @click="navigateReportsPayroll('paySlipDetail')"
                >
                  Pay Slip Detail
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-table"
                  :active="$route.path.includes('/reports/payroll/pay-sheet')"
                  @click="navigateReportsPayroll('paySheet')"
                >
                  Pay Sheet
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-bar-chart"
                  :active="$route.path.includes('/reports/payroll/pay-head-summary')"
                  @click="navigateReportsPayroll('payHeadSummary')"
                >
                  Pay Head Summary Report
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-list-ul"
                  :active="$route.path.includes('/reports/payroll/pay-head-detail')"
                  @click="navigateReportsPayroll('payHeadDetail')"
                >
                  Pay Head Detail Report
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-receipt"
                  :active="$route.path.includes('/reports/payroll/tax-summary')"
                  @click="navigateReportsPayroll('taxSummary')"
                >
                  Tax Summary Report
                </SidebarSubmenuItem>
              </div>
            </transition>
          </div>
        </transition>
        <SidebarNavItem
          icon="bi bi-gear"
          label="Settings"
          variant="settings"
          :active="activeMenu === 'settings'"
          :expanded="settingsMenuExpanded"
          :sidebar-collapsed="sidebarCollapsed"
          @click="toggleSettingsMenu"
        />
        <transition name="sidebar-submenu">
          <div v-if="settingsMenuExpanded && !sidebarCollapsed" class="customer-submenu sidebar-submenu-panel sidebar-submenu-panel--settings">
            <SidebarSubmenuItem
              icon="bi bi-building"
              :active="$route.path.includes('/settings/company')"
              @click="navigateSettingsSub('company')"
            >
              Company Settings
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-people"
              expandable
              :expanded="settingsUserManagementExpanded"
              custom-class="customer-expandable"
              :active="$route.path.includes('/settings/users') ||
                $route.path.includes('/settings/update-profile') ||
                $route.path.includes('/settings/change-password') ||
                $route.path.includes('/settings/session-alert')"
              @click="toggleSettingsUserManagement"
            >
              User Management
            </SidebarSubmenuItem>
            <transition name="sidebar-submenu">
              <div v-if="settingsUserManagementExpanded" class="customer-submenu">
                <SidebarSubmenuItem
                  icon="bi bi-people"
                  :active="$route.path.includes('/settings/users')"
                  @click="navigateSettingsSub('users')"
                >
                  Users
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-person-circle"
                  :active="$route.path.includes('/settings/update-profile')"
                  @click="navigateSettingsSub('updateprofile')"
                >
                  Update profile
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-key-fill"
                  :active="$route.path.includes('/settings/change-password')"
                  @click="navigateSettingsSub('changepassword')"
                >
                  Change password
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-alarm"
                  :active="$route.path.includes('/settings/session-alert')"
                  @click="navigateSettingsSub('sessionalert')"
                >
                  Session Alert
                </SidebarSubmenuItem>
              </div>
            </transition>
            <SidebarSubmenuItem
              icon="bi bi-envelope"
              :active="$route.path.includes('/settings/email-setup')"
              @click="navigateSettingsSub('emailsetup')"
            >
              Email Setup
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-pen"
              :active="$route.path.includes('/settings/email-signature')"
              @click="navigateSettingsSub('emailsignature')"
            >
              Email Signature
              <span v-if="currentSignatureName" class="current-signature-badge" title="Current saved signature">
                {{ currentSignatureName }}
              </span>
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-translate"
              :active="$route.path.includes('/settings/translation')"
              @click="navigateSettingsSub('translation')"
            >
              Translation
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-palette"
              :active="$route.path.includes('/settings/ui-preferences')"
              @click="navigateSettingsSub('uipreferences')"
            >
              UI Preferences
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-file-earmark-image"
              :active="$route.path.includes('/settings/document-branding')"
              @click="navigateSettingsSub('documentbranding')"
            >
              Document Branding
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-printer"
              :active="$route.path.includes('/settings/check-print-setting')"
              @click="navigateSettingsSub('checkprint')"
            >
              Check Print Setting
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-key"
              :active="$route.path.includes('/settings/integration-keys')"
              @click="navigateSettingsSub('integrationkeys')"
            >
              Integration Keys
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-bell"
              expandable
              :expanded="settingsNotificationsExpanded"
              custom-class="customer-expandable"
              :active="$route.path.includes('/settings/messaging-notifications') ||
                $route.path.includes('/settings/email-notifications')"
              @click="toggleSettingsNotifications"
            >
              Notifications
            </SidebarSubmenuItem>
            <transition name="sidebar-submenu">
              <div v-if="settingsNotificationsExpanded" class="customer-submenu">
                <SidebarSubmenuItem
                  icon="bi bi-chat-text"
                  :active="$route.path.includes('/settings/messaging-notifications')"
                  @click="navigateSettingsSub('smsnotifications')"
                >
                  SMS Notifications
                </SidebarSubmenuItem>
                <SidebarSubmenuItem
                  icon="bi bi-envelope"
                  :active="$route.path.includes('/settings/email-notifications')"
                  @click="navigateSettingsSub('emailnotifications')"
                >
                  Email Notifications
                </SidebarSubmenuItem>
              </div>
            </transition>
            <SidebarSubmenuItem
              icon="bi bi-envelope-exclamation"
              :active="$route.path.includes('/settings/dunning-letter-categories')"
              @click="navigateSettingsSub('dunninglettercategories')"
            >
              Dunning Letter
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-journal-text"
              :active="$route.path.includes('/settings/journal-entry-guide')"
              @click="navigateSettingsSub('journalentryguide')"
            >
              Journal Entry Guide
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-gear"
              :active="$route.path.includes('/settings/account-configuration')"
              @click="navigateSettingsSub('accountconfiguration')"
            >
              Account Configuration
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              v-if="!isInvitedUser"
              icon="bi bi-download"
              :active="$route.path.includes('/settings/data-export')"
              @click="navigateSettingsSub('dataexport')"
            >
              Data Export
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              v-if="!isInvitedUser"
              icon="bi bi-upload"
              :active="$route.path.includes('/settings/data-import')"
              @click="navigateSettingsSub('dataimport')"
            >
              Data Import
            </SidebarSubmenuItem>
            <SidebarSubmenuItem
              icon="bi bi-trash"
              :active="$route.path.includes('/settings/delete-company')"
              @click="navigateSettingsSub('delete')"
            >
              Delete company
            </SidebarSubmenuItem>
          </div>
        </transition>
        </div><!-- /.sidebar-nav-card -->
        </div><!-- /.sidebar-content -->
        
        <!-- User Profile Dropdown - Fixed at bottom -->
        <div class="user-profile-container">
          <UserProfileDropdown
            :user-name="displayUsername"
            :company-name="profileCompanyName"
            :profile-image-url="userProfileImageUrl"
            :is-collapsed="sidebarCollapsed"
            @menu-item-click="handleUserMenuItemClick"
          />
        </div>
      </aside>

      <!-- Resize Handle -->
      <div 
        v-if="!sidebarCollapsed && !isMobile"
        class="resize-handle"
        @mousedown="startResize"
        :class="{ 'resizing': isResizing }"
      ></div>

      <!-- Dashboard Content -->
      <div
        class="dashboard-content"
        :class="{ 'dashboard-content--anna-assistant': mascotAssistantOpen }"
      >
        <div class="dashboard-content-main">
          <PageWindowBar @open-switcher="openPageWindowSwitcher" />
          <HourglassLoader :show="loading" text="Loading..." mode="contained" />
          <div class="dashboard-router-view">
            <!-- Keep router-view mounted. Never feed a null Component into keep-alive
                 (avoids shouldUpdateComponent / emitsOptions null crashes after HMR). -->
            <router-view v-slot="{ Component, route }">
              <template v-if="Component">
                <keep-alive v-if="multiWindowEnabled" :max="20">
                  <component
                    :is="Component"
                    :key="pageWindowComponentKey(route)"
                  />
                </keep-alive>
                <component
                  v-else
                  :is="Component"
                  :key="route.fullPath"
                />
              </template>
            </router-view>
          </div>
        </div>
        <aside
          v-if="mascotAssistantOpen"
          class="dashboard-mascot-panel"
          aria-label="Assistant mascot panel"
        >
          <div
            class="dashboard-mascot-resize-handle"
            title="Drag to resize"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize mascot assistant panel"
            @mousedown.prevent="startMascotResize"
          />
          <AssistantMascot
            mascot-only
            :active="false"
            :viewer-name="displayUsername"
          />
        </aside>
      </div>
    </div>
    <SelectItemType
      v-if="showItemTypeModal"
      v-model="selectedItemType"
      @ok="handleSelectItemTypeOk"
      @cancel="handleSelectItemTypeCancel"
    />
    <ConsultantTypeSelectionModal
      :show="showConsultantTypeModal"
      @select="handleConsultantTypeSelect"
      @close="closeConsultantTypeModal"
    />
    <SelectPurchaseItem
      v-if="showPurchaseItemModal"
      v-model="selectedPurchaseItemType"
      @ok="handleSelectPurchaseItemOk"
      @close="handleSelectPurchaseItemCancel"
    />
    <RecordExpensesModal
      v-if="showRecordExpensesModal"
      v-model="selectedExpenseType"
      @ok="handleRecordExpensesOk"
      @close="handleRecordExpensesCancel"
    />

    <!-- Chart of Accounts Setup Modal -->
    <ChartOfAccountsSetupModal
      :show="showChartOfAccountsSetupModal"
      :is-loading="isChartLoading"
      :total-accounts="totalChartAccounts"
      @load-chart="handleLoadChartOfAccounts"
      @skip="handleSkipChartSetup"
    />

    <!-- Progress Tracker for Chart Loading -->
    <ProgressTracker
      :show="showChartProgressTracker"
      title="Loading Chart of Accounts"
      :total-count="chartProgressTrackerData.totalCount"
      :processed-count="chartProgressTrackerData.processedCount"
      :completed-count="chartProgressTrackerData.completedCount"
      :skipped-count="chartProgressTrackerData.skippedCount"
      :processing-count="chartProgressTrackerData.processingCount"
      :error-count="chartProgressTrackerData.errorCount"
      :errors="chartProgressTrackerData.errors"
      :is-processing="isChartLoading"
      item-label="accounts"
      @close="handleCloseChartProgressTracker"
    />

    <!-- Skip Chart Setup Confirmation Modal -->
    <DeleteConfirmationModal
      :show="showSkipChartModal"
      title="⚠️ Skip Chart of Accounts Setup?"
      message="Without a Chart of Accounts, you won't be able to properly track your business finances, transactions, and generate accurate financial reports."
      :details="{
        'Impact': 'Limited financial tracking',
        'Consequences': 'Cannot process transactions correctly',
        'Reports': 'Financial reports will be incomplete',
        'Setup Later': 'You can set it up from Company > Company List > Accounts List'
      }"
      confirm-text="Yes, Skip Anyway"
      cancel-text="Go Back"
      :show-warning="true"
      :loading="false"
      @confirm="confirmSkipChartSetup"
      @cancel="cancelSkipChartSetup"
    />

    <!-- Unified Transition Component -->
    <AppTransition 
      :show="showCompaniesTransition" 
      type="companies"
      title="Loading Companies..."
      submessage="Preparing your companies list..."
    />
    <AppTransition 
      :show="showLogoutTransition" 
      type="logout"
      title="Logging Out..."
      submessage="Thank you for using Anna Books"
    />

    <SessionNearingExpiryModal
      :show="showSessionNearingModal"
      :expires-at-ms="sessionNearingExpiresAtMs"
      :expires-label="sessionNearingExpiresLabel"
      @continue="dismissSessionNearingModal"
      @logout="onSessionNearingModalLogout"
    />

    <SessionExpiredModal
      :show="showSessionExpiredModal"
      @sign-in-again="confirmSessionExpiredAndLogout"
    />

    <BillNotesChat
      v-if="showOwnerBillNotes"
      role="client"
      :company-id="$route.params.id"
      :bill-id="activeBillNotesId"
      :my-name="displayUsername"
      :my-avatar-url="userProfileImageUrl"
      :their-name="''"
      :hide-fab="isOnVendorPortalHub"
    />
  </div>
</template>

<style src="./Dashboard.css"></style>
<style src="@/components/ui/styles/DashboardSidebar.css"></style>

<style>
/* Scoped overrides live in Dashboard.css; keep only non-nav rules below */

/* Current Signature Badge */
.current-signature-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: #ffffff;
  font-size: 0.65rem;
  font-weight: 600;
  border-radius: 10px;
  margin-left: auto;
  box-shadow: 0 2px 4px rgba(99, 102, 241, 0.3);
  white-space: nowrap;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;
  line-height: 1.2;
}

.submenu-item {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: nowrap;
  gap: 8px;
  min-width: 0;
}

.submenu-item > span:first-child {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Responsive adjustments for navigation badge */
@media (max-width: 1200px) {
  .current-signature-badge {
    max-width: 80px;
    font-size: 0.6rem;
    padding: 2px 5px;
  }
}
</style>

<script>
import axios from 'axios';
import logger from '@/utils/logger';
import { formatJwtExpiryForDisplay } from '@/utils/sessionExpiryDisplay';
import SelectItemType from '@/components/SelectItemType.vue';
import SelectPurchaseItem from '@/modules/vendor/views/SelectPurchaseItem.vue';
import RecordExpensesModal from './dashboard/vendors/RecordExpensesModal.vue';
import UserProfileDropdown from '@/components/UserProfileDropdown.vue';
import ChartOfAccountsSetupModal from '@/components/ChartOfAccountsSetupModal.vue';
import ProgressTracker from '@/components/ProgressTracker.vue';
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal.vue';
import AppTransition from '@/components/transitions/AppTransition.vue';
import HourglassLoader from '@/components/HourglassLoader.vue';
import ChartOfAccountsService from '@/modules/company/services/chartOfAccounts.service.js';
import ApiService from '@/services/api.service.js';
import { getIndustryTypeLabel } from '@/constants/companyIndustryTypes';
import ConsultantTypeSelectionModal from '@/components/ConsultantTypeSelectionModal.vue';
import AssistantMascot from '@/components/AssistantMascot.vue';
import SessionExpiryIndicator from '@/components/SessionExpiryIndicator.vue';
import SessionExpiredModal from '@/components/SessionExpiredModal.vue';
import SessionNearingExpiryModal from '@/components/SessionNearingExpiryModal.vue';
import PageWindowBar from '@/components/PageWindowBar.vue';
import PageWindowSwitcher from '@/components/PageWindowSwitcher.vue';
import BillNotesChat from '@/components/BillNotesChat.vue';
import pageWindowNavigationService from '@/utils/pageWindowNavigation.service';
import uiPreferencesService from '@/utils/uiPreferences.service';
import { useMascotAssistant } from '@/composables/useMascotAssistant';
import { usePageWindows } from '@/composables/usePageWindows';
import {
  IconButton,
  TopNavItem,
  SidebarNavItem,
  SidebarSubmenuItem,
} from '@/components/ui';

export default {
  name: 'Dashboard',
  components: {
    IconButton,
    TopNavItem,
    SidebarNavItem,
    SidebarSubmenuItem,
    SelectItemType,
    SelectPurchaseItem,
    RecordExpensesModal,
    UserProfileDropdown,
    ChartOfAccountsSetupModal,
    ProgressTracker,
    DeleteConfirmationModal,
    AppTransition,
    HourglassLoader,
    ConsultantTypeSelectionModal,
    AssistantMascot,
    SessionExpiryIndicator,
    SessionExpiredModal,
    SessionNearingExpiryModal,
    PageWindowBar,
    PageWindowSwitcher,
    BillNotesChat,
  },
  setup() {
    const { isOpen: mascotOpen } = useMascotAssistant();
    const {
      isMultiWindowEnabled: multiWindowEnabled,
      openWindows,
      openFromRoute,
      openSwitcher,
      closeSwitcher,
      clearWindows,
      pruneWindowsForCompany,
      switcherOpen,
      getRefreshKeyForPath,
    } = usePageWindows();

    return {
      mascotAssistantOpen: mascotOpen,
      multiWindowEnabled,
      openWindows,
      openFromRoute,
      openSwitcher,
      closeSwitcher,
      clearWindows,
      pruneWindowsForCompany,
      switcherOpen,
      getRefreshKeyForPath,
    };
  },
  data() {
    return {
      activeMenu: 'company',
      activeTopMenu: 'dashboard',
      company: null,
      loading: false,
      customerMenuExpanded: false,
      settingsMenuExpanded: false,
      vendorMenuExpanded: false,
      vendorNewMenuExpanded: false,
      vendorBillsMenuExpanded: false,
      vendorPayBillsMenuExpanded: false,
      inventoryMenuExpanded: false,
      inventoryNewMenuExpanded: false,
      bankingMenuExpanded: false,
      bankingCreditCardsMenuExpanded: false,
      bankingLoansMenuExpanded: false,
      bankingLoansLendersMenuExpanded: false,
      bankingOrgLoansMenuExpanded: false,
      bankingOrgLoansOrgsMenuExpanded: false,
      companyMenuExpanded: false,
      companySupportListsExpanded: false,
      companyNotesListsExpanded: false,
      companyFixedAssetsExpanded: false,
      companyMergeAccountsExpanded: false,
      companyListsExpanded: false,
      reportsMenuExpanded: false,
      reportsCompanyFinanceExpanded: false,
      reportsCustomersReceivableExpanded: false,
      reportsSalesExpanded: false,
      reportsVendorsPayablesExpanded: false,
      reportsPurchaseExpanded: false,
      reportsBudgetExpanded: false,
      reportsFixedAssetExpanded: false,
      reportsInventoryExpanded: false,
      reportsBankingExpanded: false,
      reportsPayrollExpanded: false,
      settingsNotificationsExpanded: false,
      settingsUserManagementExpanded: false,
      customerNewMenuExpanded: false,
      consultantMenuExpanded: false,
      consultantNewMenuExpanded: false,
      consultantPayrollMenuExpanded: false,
      consultantBenefitsMenuExpanded: false,
      consultantContractMenuExpanded: false,
      showCompaniesTransition: false,
      showLogoutTransition: false,
      showSessionExpiredModal: false,
      showSessionNearingModal: false,
      sessionNearingExpiresAtMs: null,
      sessionNearingExpiresLabel: '',
      showItemTypeModal: false,
      selectedItemType: 'Service Item',
      showPurchaseItemModal: false,
      selectedPurchaseItemType: 'Service Item',
      showRecordExpensesModal: false,
      selectedExpenseType: 'Credit Card',
      showConsultantTypeModal: false,
      sidebarCollapsed: false,
      mobileMenuOpen: false,
      isMobile: false,
      sidebarWidth: 380,
      isResizing: false,
      resizeStartX: 0,
      resizeStartWidth: 0,
      mascotPanelWidth: 384,
      mascotResizing: false,
      mascotResizeStartX: 0,
      mascotResizeStartWidth: 384,
      userMenuItems: [
        {
          id: 'settings',
          label: 'Personal Settings',
          icon: 'settings',
          action: 'settings'
        },
        {
          id: 'logout',
          label: 'Logout',
          icon: 'logout',
          action: 'logout'
        }
      ],
      // Chart of Accounts setup modal
      showChartOfAccountsSetupModal: false,
      chartLoaded: false,
      isChartLoading: false,
      // Progress tracker for chart loading
      showChartProgressTracker: false,
      chartProgressTrackerData: {
        totalCount: 0,
        processedCount: 0,
        completedCount: 0,
        skippedCount: 0,
        processingCount: 0,
        errorCount: 0,
        errors: []
      },
      // Skip confirmation modal
      showSkipChartModal: false,
      // Current email signature name
      currentSignatureName: null,
    }
  },
  computed: {
    dashboardCssVars() {
      const mascotWidth = Math.max(280, Math.min(680, Number(this.mascotPanelWidth) || 384));
      return {
        '--sidebar-width-current': `${this.sidebarWidth}px`,
        '--mascot-panel-width': `${mascotWidth}px`,
      };
    },
    displayUsername() {
      // Try to get username from multiple sources
      const authUser = this.$store.state.auth?.user;
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      
      // Priority order: auth store > localStorage > fallback
      if (authUser && authUser.username) {
        return authUser.username;
      }
      
      if (currentUser && currentUser.username) {
        return currentUser.username;
      }
      
      if (authUser && authUser.firstName && authUser.lastName) {
        return `${authUser.firstName} ${authUser.lastName}`;
      }
      
      if (currentUser && currentUser.firstName && currentUser.lastName) {
        return `${currentUser.firstName} ${currentUser.lastName}`;
      }
      
      if (authUser && authUser.email) {
        return authUser.email.split('@')[0]; // Use email prefix as username
      }
      
      if (currentUser && currentUser.email) {
        return currentUser.email.split('@')[0]; // Use email prefix as username
      }
      
      return 'User';
    },
    profileCompanyName() {
      const company =
        this.$store.state.companies?.currentCompany ||
        this.$store.state.currentCompany ||
        this.company;
      if (!company) {
        return '';
      }
      return (
        company.companyName ||
        company.name ||
        company.legalName ||
        ''
      );
    },
    userInitials() {
      // Generate initials from displayUsername
      const name = this.displayUsername;
      if (!name || name === 'User') return 'U';
      
      const words = name.split(' ');
      if (words.length >= 2) {
        return (words[0][0] + words[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },
    userProfileImageUrl() {
      const authUser = this.$store.state.auth?.user;
      let stored = null;
      try {
        stored = JSON.parse(localStorage.getItem('user') || 'null');
      } catch (_) { stored = null; }

      const directUrl = authUser?.profileImageUrl || stored?.profileImageUrl || '';
      if (directUrl) {
        return this.absolutizeUploadUrl(directUrl);
      }

      const fileName = authUser?.profileImage || stored?.profileImage || '';
      const userId = authUser?.id || stored?.id || null;
      if (fileName && userId) {
        return this.absolutizeUploadUrl(`/api/uploads/profile-images/${userId}/${fileName}`);
      }
      return '';
    },
    isInvitedUser() {
      const storeUser = this.$store?.getters?.['auth/currentUser']
        || this.$store?.state?.auth?.user;
      if (storeUser && storeUser.is_invited_user === true) return true;
      try {
        const stored = JSON.parse(localStorage.getItem('user') || 'null');
        return stored && stored.is_invited_user === true;
      } catch (_) {
        return false;
      }
    },
    /** Company owner only — invited members do not get vendor bill notes. */
    isCompanyOwner() {
      if (this.isInvitedUser) return false;
      const authUser = this.$store?.state?.auth?.user;
      let stored = null;
      try {
        stored = JSON.parse(localStorage.getItem('user') || 'null');
      } catch (_) {
        stored = null;
      }
      const selfId = Number(authUser?.id || stored?.id);
      const ownerId = Number(this.company?.clientId);
      if (!Number.isFinite(selfId) || !Number.isFinite(ownerId)) return false;
      return selfId === ownerId;
    },
    isVendorNotesContext() {
      // Kept for any vendor-specific note helpers; floating button uses all dashboard sections.
      const path = String(this.$route?.path || '');
      return path.includes('/dashboard/vendors');
    },
    showOwnerBillNotes() {
      // Floating notes on every client dashboard section (Consultant, Customer, Vendor, Inventory, Banking, etc.).
      // On Vendor Portal Hub the FAB is hidden (page has its own notes entry points) but the chat still mounts
      // so Invites & Notes can open it for a bill.
      const path = String(this.$route?.path || '');
      const onCompanyDashboard =
        this.$route?.name === 'CompanyDashboard' ||
        path.includes('/dashboard');
      return this.isCompanyOwner && onCompanyDashboard;
    },
    isOnVendorPortalHub() {
      return String(this.$route?.path || '').includes('/vendors/portal-hub');
    },
    activeBillNotesId() {
      const q = this.$route?.query?.billId;
      return q != null && q !== '' ? q : null;
    },
    totalChartAccounts() {
      return ChartOfAccountsService.getTotalAccountCount();
    },
    industryLabel() {
      const industryId = this.company?.industryId || this.company?.cp_industry_type;
      return getIndustryTypeLabel(industryId, { fallback: '', consultantAsLabel: true });
    },
    currentSectionName() {
      const path = this.$route.path;
      if (this.$route.name === 'CompanyDashboard') return 'Dashboard';
      if (path.includes('/dashboard/add-consultant')) return 'Add New Consultant';
      if (path.includes('/dashboard/new-inventory-assembly')) return 'Inventory Assembly';
      if (path.includes('/dashboard/new-non-inventory-item')) return 'Non Inventory Item';
      if (path.includes('/dashboard/new-inventory-item')) return 'Inventory Item';
      if (path.includes('/dashboard/new-service-item')) return 'Service Item';
      if (path.includes('/customers/center')) return 'Customer Center';
      if (path.includes('/customers/new-invoice')) return 'Add New Invoice';
      if (path.includes('/customers/create-invoice')) return 'Create Invoice';
      if (path.includes('/customers/new-credit-memo')) return 'Add New Credit Memo';
      if (path.includes('/customers/new-cash-sale')) return 'New Cash Sale';
      if (path.includes('/customers/new')) return 'New Customer';
      if (path.includes('/customers/service-item')) return 'Service Item';
      if (path.includes('/customers/prepayment')) return 'Customer Prepayment';
      if (path.includes('/customers/receive-payment')) return 'Receive Payment';
      if (path.includes('/customers/refund')) return 'Customer Refund';
      if (path.includes('/customers/list')) return 'Customers';
      if (path.includes('/customers/invoices')) return 'Invoices';
      if (path.includes('/customers/items')) return 'Customer Items';
      if (path.includes('/vendors/center')) return 'Vendor Center';
      if (path.includes('/vendors/mail-sources')) return 'Bill Mail Setup';
      if (path.includes('/vendors/draft-bills')) return 'Drafts & Captured';
      if (path.includes('/vendors/portal-hub')) return 'Invites & Submitted';
      if (path.includes('/vendors')) return 'Vendors';
      if (path.includes('/inventory')) return 'Inventory';
      if (path.includes('/banking')) return 'Banking';
      if (path.includes('/company/add-account')) return 'Add Account';
      if (path.includes('/company/lists/accounts')) return 'Accounts List';
      if (path.includes('/company-preferences')) return 'Company Preferences';
      if (path.includes('/reports')) return 'Reports';
      if (path.includes('/dashboard/overview')) return 'Home';
      if (path.includes('/company/lists/items')) return 'Both Customer And Vendor > Items';
      if (path.includes('/company/lists/salespersons')) return 'Sales Persons';
      if (path.includes('/company/add-salesperson')) return 'New Sales Person';
      if (path.includes('/company/lists/activitylog')) return 'Users Activity Log';
      if (path.includes('/company/lists/recurring')) return 'Recurring Transactions List';
      if (path.includes('/company/lists/reminders')) return 'Reminders List';
      return 'Dashboard';
    },
    billTracking() {
      return this.company && typeof this.company.billTracking !== 'undefined' ? this.company.billTracking : false;
    },
    currentRoute() {
      return this.$route.path;
    },
    isVendorCreateBillActive() {
      return this.$route.path.includes('/vendors/create-bill');
    },
    isVendorDraftBillsActive() {
      return this.$route.path.includes('/vendors/draft-bills');
    },
    isVendorMailSourcesActive() {
      return this.$route.path.includes('/vendors/mail-sources');
    },
    isVendorPayBillActive() {
      return this.$route.path.includes('/vendors/pay-bill');
    },
    isCustomerCreateInvoiceActive() {
      return this.$route.path.includes('/customers/create-invoice');
    },
    isCustomerNewInvoiceActive() {
      return this.$route.path.includes('/customers/new-invoice');
    },
    isCustomerNewActive() {
      return this.$route.path.includes('/customers/new');
    },
  },
  watch: {
    '$route': {
      immediate: true,
      handler(to, from) {
        const fromCompanyId = from?.params?.id;
        const toCompanyId = to?.params?.id;
        const leftCompanyContext =
          Boolean(fromCompanyId) && !toCompanyId;
        const companyChanged =
          Boolean(fromCompanyId) &&
          Boolean(toCompanyId) &&
          String(fromCompanyId) !== String(toCompanyId);

        // Switching companies (or leaving company context) must not keep previous company's tabs
        if (companyChanged || leftCompanyContext) {
          this.clearWindows();
          this.closeSwitcher();
        } else if (toCompanyId) {
          this.pruneWindowsForCompany(toCompanyId);
        }

        const isWindowTabSwitch =
          !companyChanged && pageWindowNavigationService.isWindowTabSwitch(from, to);

        // Window tab switch: only update right-side content — keep sidebar & top nav stable
        if (!isWindowTabSwitch) {
          this.syncSidebarAndTopNavFromRoute(to);
          this.fetchCompanyData();
          if (to.path.includes('/settings')) {
            this.fetchCurrentSignature();
          }
        }

        this.openFromRoute(to);
      }
    },
    '$store.state.currentCompany': {
      handler(company) {
        if (!company) {
          return;
        }
        const routeCompanyId = this.$route?.params?.id;
        if (routeCompanyId && String(company.id) !== String(routeCompanyId)) {
          return;
        }
        this.company = { ...(this.company || {}), ...company };
      },
      deep: true,
      immediate: true
    },
    '$store.state.companies.currentCompany': {
      handler(company) {
        if (!company) {
          return;
        }
        const routeCompanyId = this.$route?.params?.id;
        if (routeCompanyId && String(company.id) !== String(routeCompanyId)) {
          return;
        }
        this.company = { ...(this.company || {}), ...company };
        this.$store.commit('SET_CURRENT_COMPANY', {
          ...(this.$store.state.currentCompany || {}),
          ...company
        });
      },
      deep: true,
      immediate: true
    },
  },
  created() {
    this.fetchCompanyData();
    this.fetchCurrentSignature();
    this.checkMobileView();
    this.applyUIMode();
    this.loadSidebarWidth();
    this.loadMascotPanelWidth();
    window.addEventListener('resize', this.checkMobileView);
    window.addEventListener('uiModeChanged', this.handleUIModeChange);
    window.addEventListener('pageWindowModeChanged', this.handlePageWindowModeChange);
  },
  
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobileView);
    window.removeEventListener('uiModeChanged', this.handleUIModeChange);
    window.removeEventListener('pageWindowModeChanged', this.handlePageWindowModeChange);
    // Clean up resize event listeners
    document.removeEventListener('mousemove', this.handleResize);
    document.removeEventListener('mouseup', this.stopResize);
    document.removeEventListener('mousemove', this.handleMascotResize);
    document.removeEventListener('mouseup', this.stopMascotResize);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  },
  methods: {
    handlePageWindowModeChange(event) {
      const mode = event.detail?.mode;
      if (mode === 'single') {
        this.clearWindows();
        this.closeSwitcher();
      } else if (mode === 'multi') {
        // Register current page as first window immediately after enabling
        this.$nextTick(() => {
          this.openFromRoute(this.$route);
        });
      }
    },
    openPageWindowSwitcher() {
      this.openSwitcher();
    },
    pageWindowComponentKey(route) {
      if (!route?.fullPath) return 'page';
      if (!this.multiWindowEnabled) return route.fullPath;
      // Touch openWindows so refreshKey bumps re-render this keep-alive key
      const win = (this.openWindows || []).find((w) => w.fullPath === route.fullPath);
      const refreshKey = win?.refreshKey || 0;
      return `${route.fullPath}::r${refreshKey}`;
    },
    syncSidebarAndTopNavFromRoute(to) {
      this.syncActiveTopMenuFromRoute(to);
      if (to.path.includes('/customers')) {
        this.activeMenu = 'customer';
        this.customerMenuExpanded = true;
      } else if (to.path.includes('/vendors')) {
        this.activeMenu = 'vendor';
        this.vendorMenuExpanded = true;
        if (
          to.path.includes('/vendors/enter-bill') ||
          to.path.includes('/vendors/create-bill') ||
          to.path.includes('/vendors/draft-bills') ||
          to.path.includes('/vendors/mail-sources')
        ) {
          this.vendorBillsMenuExpanded = true;
        }
        if (
          to.path.includes('/vendors/pay-bill') ||
          to.path.includes('/vendors/payments')
        ) {
          this.vendorPayBillsMenuExpanded = true;
        }
      } else if (to.name === 'CompanyDashboard') {
        this.activeMenu = 'company';
      } else if (to.path.includes('/dashboard/overview')) {
        this.activeMenu = 'company';
      } else if (to.path.includes('/overview')) {
        this.activeMenu = 'company';
      } else if (to.path.includes('/consultant')) {
        this.activeMenu = 'consultant';
        this.consultantMenuExpanded = true;
        if (to.path.includes('/consultant/payroll')) {
          this.consultantPayrollMenuExpanded = true;
        }
        if (to.path.includes('/consultant/benefits')) {
          this.consultantBenefitsMenuExpanded = true;
        }
        if (
          to.path.includes('/consultant/contract-details') ||
          to.path.includes('/consultant/contract-tracking')
        ) {
          this.consultantContractMenuExpanded = true;
        }
      } else if (to.path.includes('/dashboard/settings')) {
        this.activeMenu = 'settings';
        this.settingsMenuExpanded = true;
        this.settingsNotificationsExpanded =
          to.path.includes('/settings/messaging-notifications') ||
          to.path.includes('/settings/email-notifications');
        this.settingsUserManagementExpanded =
          to.path.includes('/settings/users') ||
          to.path.includes('/settings/update-profile') ||
          to.path.includes('/settings/change-password') ||
          to.path.includes('/settings/session-alert');
      } else if (to.path.includes('/banking')) {
        this.activeMenu = 'banking';
        this.bankingMenuExpanded = true;
        if (to.path.includes('/banking/credit-cards')) {
          this.bankingCreditCardsMenuExpanded = true;
        }
        if (to.path.includes('/banking/loans') && !to.path.includes('/banking/organization-loans')) {
          this.bankingLoansMenuExpanded = true;
          if (to.path.includes('/banking/loans/lenders')) {
            this.bankingLoansLendersMenuExpanded = true;
          }
        }
        if (to.path.includes('/banking/organization-loans')) {
          this.bankingOrgLoansMenuExpanded = true;
          if (to.path.includes('/banking/organization-loans/organizations')) {
            this.bankingOrgLoansOrgsMenuExpanded = true;
          }
        }
      } else if (to.path.includes('/inventory')) {
        this.activeMenu = 'inventory';
        this.inventoryMenuExpanded = true;
      } else if (to.path.includes('/reports')) {
        this.activeMenu = 'reports';
        this.reportsMenuExpanded = true;
      }
    },
    syncActiveTopMenuFromRoute(to) {
      const path = to?.path || '';
      const name = to?.name || '';

      if (path.includes('/dashboard/tracker')) {
        this.activeTopMenu = 'tracker';
        return;
      }
      if (path.includes('/dashboard/reports') || path.includes('/reports/')) {
        this.activeTopMenu = 'reports';
        return;
      }
      if (path.includes('/dashboard/company-preferences')) {
        this.activeTopMenu = 'preferences';
        return;
      }
      if (path.includes('/dashboard/overview')) {
        this.activeTopMenu = 'home';
        return;
      }
      if (path === '/home' || name === 'Home') {
        this.activeTopMenu = 'companies';
        return;
      }
      if (name === 'CompanyDashboard' || /\/company\/[^/]+\/dashboard\/?$/.test(path)) {
        this.activeTopMenu = 'dashboard';
        return;
      }
    },
    absolutizeUploadUrl(relPath) {
      if (!relPath) return '';
      if (/^https?:/i.test(relPath)) return relPath;
      const base = (process.env.VUE_APP_API_URL || '').replace(/\/$/, '');
      const host = base.replace(/\/api$/i, '');
      const cleanRel = String(relPath).startsWith('/') ? relPath : `/${relPath}`;
      const pathWithApi = /^\/api(\/|$)/i.test(cleanRel) ? cleanRel : `/api${cleanRel}`;
      return `${host}${pathWithApi}`;
    },
    async fetchCompanyData() {
      try {
        this.loading = true;
        const companyId = this.$route.params.id;
        if (!companyId) {
          this.company = null;
          return;
        }

        const companyFromModule = this.$store.state.companies?.currentCompany;
        const companyFromStore = this.$store.state.currentCompany;
        const cachedCompany =
          (companyFromModule && String(companyFromModule.id) === String(companyId) && companyFromModule) ||
          (companyFromStore && String(companyFromStore.id) === String(companyId) && companyFromStore) ||
          null;

        // Always refresh from API so renamed companies update the sidebar profile
        const response = await axios.get(`/company/${companyId}`);
        const companyData = response?.data?.company || response?.data || cachedCompany;
        if (!companyData) {
          this.company = null;
          return;
        }

        this.company = companyData;
        this.$store.commit('SET_CURRENT_COMPANY', companyData);
        this.$store.commit('companies/SET_CURRENT_COMPANY', companyData);

        await this.checkChartOfAccounts(companyId);
      } catch (error) {
        logger.error('Error fetching company data:', error);
        // Keep cached company during backend restarts / transient network errors
        const companyId = this.$route.params.id;
        const companyFromModule = this.$store.state.companies?.currentCompany;
        const companyFromStore = this.$store.state.currentCompany;
        const cachedCompany =
          (companyFromModule && String(companyFromModule.id) === String(companyId) && companyFromModule) ||
          (companyFromStore && String(companyFromStore.id) === String(companyId) && companyFromStore) ||
          this.company ||
          null;
        if (cachedCompany) {
          this.company = cachedCompany;
          return;
        }
        this.company = null;
        this.$toast && this.$toast.error ? this.$toast.error('Failed to load company data') : alert('Failed to load company data');
      } finally {
        this.loading = false;
      }
    },
    async fetchCurrentSignature() {
      try {
        const companyId = this.$route.params.id;
        if (!companyId) return;
        
        const response = await axios.get(`/company/${companyId}/email-signatures`);
        
        if (response.data && response.data.signatures && response.data.signatures.length > 0) {
          // Find default signature or use first one
          const defaultSig = response.data.signatures.find(s => s.isDefault) || response.data.signatures[0];
          this.currentSignatureName = defaultSig.name || null;
        } else {
          this.currentSignatureName = null;
        }
      } catch (error) {
        // If 404 or no signatures, that's okay - just set to null
        if (error.response && error.response.status !== 404) {
          logger.error('Error fetching current signature:', error);
        }
        this.currentSignatureName = null;
      }
    },
    async checkChartOfAccounts(companyId) {
      try {
        const res = await ApiService.getCompanyAccounts(companyId);
        const accounts = res.data.accounts || res.data || [];
        
        // Check if any predefined chart accounts exist
        const predefinedAccountNumbers = [
          '1000', '1001', '1010', '1011', '1012', '1020', '1200', '1201', '1202', '1203',
          '1300', '1301', '1302', '1400', '1401', '1402', '1500', '1501', '1502', '1503', '1504',
          '1600', '1601', '2000', '2001', '2002', '2003', '2004', '2005', '2100', '2101', '2102', '2103', '2104',
          '2200', '2201', '2202', '2203', '3000', '3001', '3002', '3003', '3004', '3005',
          '4000', '4001', '4002', '4003', '4100', '4101', '4102', '5000', '5001', '5002', '5003', '5004', '5005', '5006', '5007',
          '5100', '5101', '5102', '6000', '6001', '6002', '6003'
        ];
        
        const hasPredefinedAccounts = accounts.some(account => 
          predefinedAccountNumbers.includes(account.accountNumber || account.no)
        );
        
        this.chartLoaded = hasPredefinedAccounts;
        
        // Show setup modal if chart not loaded - show every time user visits dashboard until action taken
        if (hasPredefinedAccounts) {
          // Chart is loaded, close the modal if it's open
          if (this.showChartOfAccountsSetupModal) {
            this.showChartOfAccountsSetupModal = false;
          }
        } else if (!this.showChartOfAccountsSetupModal) {
          // Chart not loaded, show the modal
          this.showChartOfAccountsSetupModal = true;
        }
      } catch (error) {
        logger.error('Error checking chart of accounts:', error);
        // Don't show modal if there's an error
      }
    },
    async handleLoadChartOfAccounts() {
      // Close the setup modal first
      this.showChartOfAccountsSetupModal = false;
      
      this.isChartLoading = true;
      
      try {
        const companyId = this.$route.params.id;
        const totalAccounts = ChartOfAccountsService.getTotalAccountCount();
        
        // Initialize progress tracker
        this.chartProgressTrackerData = {
          totalCount: totalAccounts,
          processedCount: 0,
          completedCount: 0,
          skippedCount: 0,
          processingCount: totalAccounts,
          errorCount: 0,
          errors: []
        };
        this.showChartProgressTracker = true;
        
        // Use the service to create chart of accounts with progress callback
        const result = await ChartOfAccountsService.createChartOfAccounts(companyId, (progress) => {
          this.chartProgressTrackerData = { ...progress };
        });
        
        // Processing complete
        this.isChartLoading = false;

        // Show result message using toast
        if (result.errorCount === 0 && result.skippedCount === 0) {
          this.$toast && this.$toast.success(`Successfully created ${result.successCount} accounts!`);
        } else if (result.errorCount === 0) {
          this.$toast && this.$toast.success(`Created ${result.successCount} accounts successfully. ${result.skippedCount} duplicate accounts were skipped.`);
        } else {
          this.$toast && this.$toast.warning(`Created ${result.successCount} accounts. ${result.skippedCount} duplicates skipped. ${result.errorCount} failed.`);
        }

        // Mark chart as loaded
        this.chartLoaded = true;
        
      } catch (error) {
        logger.error('Error loading chart of accounts:', error);
        this.$toast && this.$toast.error(error.message || 'Failed to load chart of accounts.');
        this.isChartLoading = false;
      }
    },
    handleSkipChartSetup() {
      // Show custom modal for warning
      this.showSkipChartModal = true;
    },
    confirmSkipChartSetup() {
      this.showSkipChartModal = false;
      this.showChartOfAccountsSetupModal = false;
    },
    cancelSkipChartSetup() {
      this.showSkipChartModal = false;
    },
    handleCloseChartProgressTracker() {
      this.showChartProgressTracker = false;
      this.chartProgressTrackerData = {
        totalCount: 0,
        processedCount: 0,
        completedCount: 0,
        skippedCount: 0,
        processingCount: 0,
        errorCount: 0,
        errors: []
      };
      this.showChartOfAccountsSetupModal = false;
    },
    handleSessionNearingExpiry(payload) {
      const exp = payload && payload.expiresAtMs;
      if (!exp) return;
      if (this.showSessionExpiredModal) return;
      if (this.showSessionNearingModal && this.sessionNearingExpiresAtMs === exp) return;
      this.sessionNearingExpiresAtMs = exp;
      this.sessionNearingExpiresLabel = formatJwtExpiryForDisplay(exp);
      this.showSessionNearingModal = true;
    },
    dismissSessionNearingModal() {
      this.showSessionNearingModal = false;
      this.sessionNearingExpiresAtMs = null;
      this.sessionNearingExpiresLabel = '';
    },
    onSessionNearingModalLogout() {
      this.dismissSessionNearingModal();
      this.handleLogout();
    },
    handleSessionExpired() {
      this.showSessionNearingModal = false;
      this.sessionNearingExpiresAtMs = null;
      this.sessionNearingExpiresLabel = '';
      this.showSessionExpiredModal = true;
    },
    confirmSessionExpiredAndLogout() {
      this.showSessionExpiredModal = false;
      this.handleLogout();
    },
    async handleLogout() {
      try {
        // Show logout transition
        this.showLogoutTransition = true;
        
        // Wait for transition animation then logout
        setTimeout(async () => {
          await this.$store.dispatch('auth/logout');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          
          // Navigate to login after logout
          this.$router.push('/login');
          
          // Hide transition after navigation
          setTimeout(() => {
            this.showLogoutTransition = false;
          }, 300);
        }, 2000); // 2 second delay for smooth transition
      } catch (error) {
        logger.error('Logout error:', error);
        // Still try to redirect to login even if there's an error
        this.showLogoutTransition = false;
        this.$router.push('/login');
      }
    },
    navigateToCustomerSection() {
      this.$router.push(`/company/${this.$route.params.id}/dashboard/customers`);
    },
    handleMenuClick(menu) {
      this.activeMenu = menu;
      const companyId = this.$route.params.id;
      
      switch(menu) {
        case 'company':
          this.$router.push(`/company/${companyId}/dashboard/overview`);
          break;
        case 'customer':
          this.$router.push(`/company/${companyId}/dashboard/customers/list`);
          break;
        // Add other cases as needed
        default:
          // For now, other menu items will redirect to overview
          this.$router.push(`/company/${companyId}/dashboard/overview`);
      }
    },
    handleTopMenuClick(menu) {
      this.activeTopMenu = menu;
      this.closeMobileMenu();
      const companyId = this.$route.params.id;
      switch(menu) {
        case 'dashboard':
          this.$router.push(`/company/${companyId}/dashboard`);
          break;
        case 'companies':
          this.clearWindows();
          this.closeSwitcher();
          this.showCompaniesTransition = true;
          setTimeout(() => {
            this.$router.push('/home');
            setTimeout(() => {
              this.showCompaniesTransition = false;
            }, 300);
          }, 2000);
          break;
        case 'home':
          this.$router.push(`/company/${companyId}/dashboard/overview`);
          break;
        case 'reports':
          this.$router.push(`/company/${companyId}/dashboard/reports/home`);
          break;
        case 'tracker':
          this.$router.push(`/company/${companyId}/dashboard/tracker`);
          break;
        case 'transactions':
          this.$router.push(`/company/${companyId}/dashboard/transactions`);
          break;
        case 'preferences':
          this.$router.push(`/company/${companyId}/dashboard/company-preferences`);
          break;
        case 'vendor':
          this.$router.push(`/company/${companyId}/dashboard/vendors/center`);
          break;
        case 'customer':
          this.$router.push(`/company/${companyId}/dashboard/customers/center`);
          break;
        default:
          this.$router.push(`/company/${companyId}/dashboard/overview`);
      }
    },
    toggleCustomerMenu() {
      this.customerMenuExpanded = !this.customerMenuExpanded;
      this.activeMenu = 'customer';
    },
    toggleSettingsMenu() {
      this.settingsMenuExpanded = !this.settingsMenuExpanded;
      this.activeMenu = 'settings';
    },
    toggleSettingsNotifications() {
      this.settingsNotificationsExpanded = !this.settingsNotificationsExpanded;
      this.activeMenu = 'settings';
      this.settingsMenuExpanded = true;
    },
    toggleSettingsUserManagement() {
      this.settingsUserManagementExpanded = !this.settingsUserManagementExpanded;
      this.activeMenu = 'settings';
      this.settingsMenuExpanded = true;
    },
    toggleVendorMenu() {
      this.vendorMenuExpanded = !this.vendorMenuExpanded;
      this.activeMenu = 'vendor';
    },
    toggleVendorNewMenu() {
      this.vendorNewMenuExpanded = !this.vendorNewMenuExpanded;
    },
    toggleVendorBillsMenu() {
      this.vendorBillsMenuExpanded = !this.vendorBillsMenuExpanded;
    },
    toggleVendorPayBillsMenu() {
      this.vendorPayBillsMenuExpanded = !this.vendorPayBillsMenuExpanded;
    },
    toggleInventoryMenu() {
      this.inventoryMenuExpanded = !this.inventoryMenuExpanded;
      this.activeMenu = 'inventory';
    },
    toggleInventoryNewMenu() {
      this.inventoryNewMenuExpanded = !this.inventoryNewMenuExpanded;
    },
    toggleBankingMenu() {
      this.bankingMenuExpanded = !this.bankingMenuExpanded;
      this.activeMenu = 'banking';
    },
    toggleBankingCreditCardsMenu() {
      this.bankingCreditCardsMenuExpanded = !this.bankingCreditCardsMenuExpanded;
      this.bankingMenuExpanded = true;
      this.activeMenu = 'banking';
    },
    toggleBankingLoansMenu() {
      this.bankingLoansMenuExpanded = !this.bankingLoansMenuExpanded;
      this.bankingMenuExpanded = true;
      this.activeMenu = 'banking';
    },
    toggleBankingLoansLendersMenu() {
      this.bankingLoansLendersMenuExpanded = !this.bankingLoansLendersMenuExpanded;
      this.bankingLoansMenuExpanded = true;
      this.bankingMenuExpanded = true;
      this.activeMenu = 'banking';
    },
    toggleBankingOrgLoansMenu() {
      this.bankingOrgLoansMenuExpanded = !this.bankingOrgLoansMenuExpanded;
      this.bankingMenuExpanded = true;
      this.activeMenu = 'banking';
    },
    toggleBankingOrgLoansOrgsMenu() {
      this.bankingOrgLoansOrgsMenuExpanded = !this.bankingOrgLoansOrgsMenuExpanded;
      this.bankingOrgLoansMenuExpanded = true;
      this.bankingMenuExpanded = true;
      this.activeMenu = 'banking';
    },
    toggleCompanyMenu() {
      this.companyMenuExpanded = !this.companyMenuExpanded;
      this.activeMenu = 'company';
    },
    toggleCompanySupportLists() {
      this.companySupportListsExpanded = !this.companySupportListsExpanded;
    },
    toggleCompanyNotesLists() {
      this.companyNotesListsExpanded = !this.companyNotesListsExpanded;
    },
    toggleCompanyFixedAssets() {
      this.companyFixedAssetsExpanded = !this.companyFixedAssetsExpanded;
    },
    toggleCompanyMergeAccounts() {
      this.companyMergeAccountsExpanded = !this.companyMergeAccountsExpanded;
    },
    toggleCompanyLists() {
      this.companyListsExpanded = !this.companyListsExpanded;
    },
    toggleReportsMenu() {
      this.reportsMenuExpanded = !this.reportsMenuExpanded;
      this.activeMenu = 'reports';
    },
    toggleReportsCompanyFinance() {
      this.reportsCompanyFinanceExpanded = !this.reportsCompanyFinanceExpanded;
    },
    toggleReportsCustomersReceivable() {
      this.reportsCustomersReceivableExpanded = !this.reportsCustomersReceivableExpanded;
    },
    toggleReportsSales() {
      this.reportsSalesExpanded = !this.reportsSalesExpanded;
    },
    toggleReportsVendorsPayables() {
      this.reportsVendorsPayablesExpanded = !this.reportsVendorsPayablesExpanded;
    },
    toggleReportsPurchase() {
      this.reportsPurchaseExpanded = !this.reportsPurchaseExpanded;
    },
    toggleReportsBudget() {
      this.reportsBudgetExpanded = !this.reportsBudgetExpanded;
    },
    toggleReportsFixedAsset() {
      this.reportsFixedAssetExpanded = !this.reportsFixedAssetExpanded;
    },
    toggleReportsInventory() {
      this.reportsInventoryExpanded = !this.reportsInventoryExpanded;
    },
    toggleReportsBanking() {
      this.reportsBankingExpanded = !this.reportsBankingExpanded;
    },
    toggleReportsPayroll() {
      this.reportsPayrollExpanded = !this.reportsPayrollExpanded;
    },
    toggleCustomerNewMenu() {
      this.customerNewMenuExpanded = !this.customerNewMenuExpanded;
    },
    navigateCustomerSub(sub) {
      const companyId = this.$route.params.id;
      this.activeMenu = 'customer';
      this.customerMenuExpanded = true;
      this.closeMobileMenu();
      switch(sub) {
        case 'center':
          this.$router.push(`/company/${companyId}/dashboard/customers/center`); break;
        case 'new':
          this.$router.push(`/company/${companyId}/dashboard/customers/new`); break;
        case 'prepayment':
          this.$router.push(`/company/${companyId}/dashboard/customers/prepayment`); break;
        case 'receive':
          this.$router.push(`/company/${companyId}/dashboard/customers/receive-payment`); break;
        case 'refund':
          this.$router.push(`/company/${companyId}/dashboard/customers/refund`); break;
        case 'customers':
          this.$router.push(`/company/${companyId}/dashboard/customers/list`); break;
        case 'invoices':
          this.$router.push(`/company/${companyId}/dashboard/customers/invoices`); break;
        case 'items':
          this.$router.push(`/company/${companyId}/dashboard/customers/items`); break;
      }
    },
    navigateSettingsSub(sub) {
      const companyId = this.$route.params.id;
      this.activeMenu = 'settings';
      this.settingsMenuExpanded = true;
      this.settingsNotificationsExpanded =
        sub === 'smsnotifications' || sub === 'emailnotifications';
      this.settingsUserManagementExpanded =
        sub === 'users' ||
        sub === 'updateprofile' ||
        sub === 'changepassword' ||
        sub === 'sessionalert';
      switch(sub) {
        case 'company':
          this.$router.push(`/company/${companyId}/dashboard/company-preferences`);
          break;
        case 'users':
          this.$router.push(`/company/${companyId}/dashboard/settings/users`);
          break;
        case 'changepassword':
          this.$router.push(`/company/${companyId}/dashboard/settings/change-password`);
          break;
        case 'updateprofile':
          this.$router.push(`/company/${companyId}/dashboard/settings/update-profile`);
          break;
        case 'emailsetup':
          this.$router.push(`/company/${companyId}/dashboard/settings/email-setup`);
          break;
        case 'emailsignature':
          this.$router.push(`/company/${companyId}/dashboard/settings/email-signature`);
          break;
        case 'translation':
          this.$router.push(`/company/${companyId}/dashboard/settings/translation`);
          break;
        case 'uipreferences':
          this.$router.push(`/company/${companyId}/dashboard/settings/ui-preferences`);
          break;
        case 'sessionalert':
          this.$router.push(`/company/${companyId}/dashboard/settings/session-alert`);
          break;
        case 'documentbranding':
          logger.log('Navigating to document branding...');
          logger.log('Company ID:', companyId);
          logger.log('Target route:', `/company/${companyId}/dashboard/settings/document-branding`);
          this.$router.push(`/company/${companyId}/dashboard/settings/document-branding`);
          break;
        case 'checkprint':
          this.$router.push(`/company/${companyId}/dashboard/settings/check-print`);
          break;
        case 'integrationkeys':
          this.$router.push(`/company/${companyId}/dashboard/settings/integration-keys`);
          break;
        case 'smsnotifications':
          this.$router.push(`/company/${companyId}/dashboard/settings/messaging-notifications`);
          break;
        case 'emailnotifications':
          this.$router.push(`/company/${companyId}/dashboard/settings/email-notifications`);
          break;
        case 'dunninglettercategories':
          this.$router.push(`/company/${companyId}/dashboard/settings/dunning-letter-categories`);
          break;
        case 'accountconfiguration':
          this.$router.push(`/company/${companyId}/dashboard/settings/account-configuration`);
          break;
        case 'dataexport':
          this.$router.push(`/company/${companyId}/dashboard/settings/data-export`);
          break;
        case 'dataimport':
          this.$router.push(`/company/${companyId}/dashboard/settings/data-import`);
          break;
        case 'journalentryguide':
          this.$router.push(`/company/${companyId}/dashboard/settings/journal-entry-guide`);
          break;
      }
    },
    navigateVendorSub(sub) {
      this.activeMenu = 'vendor';
      this.vendorMenuExpanded = true;
      this.closeMobileMenu();
      const companyId = this.$route.params.id;
      switch(sub) {
        case 'center':
          this.$router.push(`/company/${companyId}/dashboard/vendors/center`);
          break;
        case 'newvendor':
          this.$router.push(`/company/${companyId}/dashboard/vendors/new-vendor`);
          break;
        case 'newpurchaseitem':
          this.showPurchaseItemModal = true;
          break;
        case 'cashpurchase':
          this.$router.push(`/company/${companyId}/dashboard/vendors/cash-purchase`);
          break;
        case 'vendorcredit':
          this.$router.push(`/company/${companyId}/dashboard/vendors/vendor-credit`);
          break;
        case 'writecheck':
          this.$router.push(`/company/${companyId}/dashboard/vendors/write-check`);
          break;
        case 'printcheck':
          this.$router.push(`/company/${companyId}/dashboard/vendors/print-check`);
          break;
        case 'enterbill':
          this.vendorBillsMenuExpanded = true;
          this.$router.push(`/company/${companyId}/dashboard/vendors/enter-bill`);
          break;
        case 'createbill':
          this.vendorBillsMenuExpanded = true;
          this.$router.push(`/company/${companyId}/dashboard/vendors/create-bill`);
          break;
        case 'draftbills':
          this.vendorBillsMenuExpanded = true;
          this.$router.push({
            name: 'DraftBills',
            params: { id: String(companyId) },
          }).catch(() => {});
          break;
        case 'mailsources':
          this.vendorBillsMenuExpanded = true;
          this.$router.push({
            name: 'VendorMailSources',
            params: { id: String(companyId) },
          }).catch(() => {});
          break;
        case 'paybill':
          this.vendorPayBillsMenuExpanded = true;
          this.$router.push(`/company/${companyId}/dashboard/vendors/pay-bill`);
          break;
        case 'bills':
          this.$router.push(`/company/${companyId}/dashboard/vendors/bills`);
          break;
        case 'expenses':
          this.showRecordExpensesModal = true;
          break;
        case 'vendors':
          this.$router.push(`/company/${companyId}/dashboard/vendors/list`);
          break;
        case 'portalhub':
          this.$router.push(`/company/${companyId}/dashboard/vendors/portal-hub`);
          break;
        case 'items':
          this.$router.push(`/company/${companyId}/dashboard/vendors/items`);
          break;
        case 'payments':
          this.vendorPayBillsMenuExpanded = true;
          this.$router.push(`/company/${companyId}/dashboard/vendors/payments`);
          break;
      }
    },
    navigateInventorySub(sub) {
      this.activeMenu = 'inventory';
      this.inventoryMenuExpanded = true;
      switch(sub) {
        case 'centre':
          this.$router.push(`/company/${this.$route.params.id}/dashboard/inventory/center`);
          break;
        case 'buildassembly':
          this.$router.push(`/company/${this.$route.params.id}/dashboard/inventory/build-assembly`);
          break;
        case 'stockadjustment':
          this.$router.push(`/company/${this.$route.params.id}/dashboard/inventory/stock-adjustment/new`);
          break;
        case 'stockadjustmentnew':
          this.$router.push(`/company/${this.$route.params.id}/dashboard/inventory/stock-adjustment/new`);
          break;
        case 'newitem':
          this.$router.push(`/company/${this.$route.params.id}/dashboard/inventory/new-item`);
          break;
        case 'newassembly':
          this.$router.push(`/company/${this.$route.params.id}/dashboard/inventory/new-assembly`);
          break;
        case 'items':
          this.$router.push(`/company/${this.$route.params.id}/dashboard/inventory/items`);
          break;
        case 'adjustments':
          this.$router.push(`/company/${this.$route.params.id}/dashboard/inventory/stock-adjustment`);
          break;
      }
    },
    navigateBankingSub(sub) {
      this.activeMenu = 'banking';
      this.bankingMenuExpanded = true;
      const companyId = this.$route.params.id;
      switch(sub) {
        case 'newaccount':
          this.$router.push(`/company/${companyId}/dashboard/banking/new-account`);
          break;
        case 'writecheck':
          this.$router.push(`/company/${companyId}/dashboard/vendors/write-check`);
          break;
        case 'transferfund':
          this.$router.push(`/company/${companyId}/dashboard/banking/transfer-fund`);
          break;
        case 'paycreditcard':
          this.$router.push(`/company/${companyId}/dashboard/banking/pay-credit-card`);
          break;
        case 'addcreditcard':
          this.bankingCreditCardsMenuExpanded = true;
          this.$router.push(`/company/${companyId}/dashboard/banking/credit-cards/add`);
          break;
        case 'creditcardsdetails':
          this.bankingCreditCardsMenuExpanded = true;
          this.$router.push(`/company/${companyId}/dashboard/banking/credit-cards/details`);
          break;
        case 'addloan':
          this.bankingLoansMenuExpanded = true;
          this.$router.push(`/company/${companyId}/dashboard/banking/loans/add`);
          break;
        case 'loansdetails':
          this.bankingLoansMenuExpanded = true;
          this.$router.push(`/company/${companyId}/dashboard/banking/loans/details`);
          break;
        case 'recordloanpayment':
          this.bankingLoansMenuExpanded = true;
          this.$router.push(`/company/${companyId}/dashboard/banking/loans/record-payment`);
          break;
        case 'addloanlender':
          this.bankingLoansMenuExpanded = true;
          this.bankingLoansLendersMenuExpanded = true;
          this.$router.push(`/company/${companyId}/dashboard/banking/loans/lenders/add`);
          break;
        case 'loanlendersdetails':
          this.bankingLoansMenuExpanded = true;
          this.bankingLoansLendersMenuExpanded = true;
          this.$router.push(`/company/${companyId}/dashboard/banking/loans/lenders/details`);
          break;
        case 'addorgloan':
          this.bankingOrgLoansMenuExpanded = true;
          this.$router.push(`/company/${companyId}/dashboard/banking/organization-loans/add`);
          break;
        case 'orgloansdetails':
          this.bankingOrgLoansMenuExpanded = true;
          this.$router.push(`/company/${companyId}/dashboard/banking/organization-loans/details`);
          break;
        case 'recordorgloanpayment':
          this.bankingOrgLoansMenuExpanded = true;
          this.$router.push(`/company/${companyId}/dashboard/banking/organization-loans/record-payment`);
          break;
        case 'recordorgloancollection':
          this.bankingOrgLoansMenuExpanded = true;
          this.$router.push(`/company/${companyId}/dashboard/banking/organization-loans/record-collection`);
          break;
        case 'addorgloanlender':
          this.bankingOrgLoansMenuExpanded = true;
          this.bankingOrgLoansOrgsMenuExpanded = true;
          this.$router.push(`/company/${companyId}/dashboard/banking/organization-loans/organizations/add`);
          break;
        case 'orgloanlendersdetails':
          this.bankingOrgLoansMenuExpanded = true;
          this.bankingOrgLoansOrgsMenuExpanded = true;
          this.$router.push(`/company/${companyId}/dashboard/banking/organization-loans/organizations/details`);
          break;
        case 'makedeposit':
          this.$router.push(`/company/${companyId}/dashboard/banking/make-deposit`);
          break;
        case 'reconhistory':
          this.$router.push(`/company/${companyId}/dashboard/banking/reconciliations-history`);
          break;
        case 'accounts':
          this.$router.push(`/company/${companyId}/dashboard/banking/accounts`);
          break;
      }
    },
    navigateCompanySub(sub) {
      this.activeMenu = 'company';
      this.companyMenuExpanded = true;
      const companyId = this.$route.params.id;
      switch(sub) {
        case 'dashboard':
          this.$router.push(`/company/${companyId}/dashboard`);
          break;
        case 'importcsv':
          this.$router.push(`/company/${companyId}/dashboard/importcsv`);
          break;
        case 'import-staging':
          this.$router.push(`/company/${companyId}/dashboard/import-staging`);
          break;
        case 'journalentry':
          this.$router.push(`/company/${companyId}/dashboard/journalentry`);
          break;
        case 'transactionscenter':
          this.$router.push(`/company/${companyId}/dashboard/transactions`);
          break;
        case 'newaccount':
          this.$router.push(`/company/${companyId}/dashboard/new-account`);
          break;
        case 'preferences':
          this.$router.push(`/company/${companyId}/dashboard/company-preferences`);
          break;
        case 'budget':
          this.$router.push(`/company/${companyId}/dashboard/company/budget`);
          break;
      }
    },
    navigateCompanySupportList(type) {
      this.activeMenu = 'company';
      this.companyMenuExpanded = true;
      this.companySupportListsExpanded = true;
      const companyId = this.$route.params.id;
      switch(type) {
        case 'customer':
          this.$router.push(`/company/${companyId}/dashboard/company/support-lists/customer-group`);
          break;
        case 'vendor':
          this.$router.push(`/company/${companyId}/dashboard/company/support-lists/vendor-group`);
          break;
        case 'invoice-frequency':
          this.$router.push(`/company/${companyId}/dashboard/company/support-lists/invoice-frequency`);
          break;
        case 'timesheet-frequency':
          this.$router.push(`/company/${companyId}/dashboard/company/support-lists/timesheet-frequency`);
          break;
        case 'payment':
          this.$router.push(`/company/${companyId}/dashboard/company/support-lists/payment-term`);
          break;
        case 'item':
          this.$router.push(`/company/${companyId}/dashboard/company/support-lists/item-group`);
          break;
        case 'warehouses':
          this.$router.push(`/company/${companyId}/dashboard/company/support-lists/warehouses`);
          break;
        case 'shipping':
          this.$router.push(`/company/${companyId}/dashboard/company/support-lists/shipping-method`);
          break;
        case 'notes-list':
          this.companyNotesListsExpanded = true;
          this.$router.push(`/company/${companyId}/dashboard/company/support-lists/notes-list`);
          break;
        case 'vendor-notes-list':
          this.companyNotesListsExpanded = true;
          this.$router.push(`/company/${companyId}/dashboard/company/support-lists/vendor-notes-list`);
          break;
        case 'note-tags':
          this.companyNotesListsExpanded = true;
          this.$router.push(`/company/${companyId}/dashboard/company/support-lists/note-tags`);
          break;
        case 'note-types':
          this.companyNotesListsExpanded = true;
          this.$router.push(`/company/${companyId}/dashboard/company/support-lists/note-types`);
          break;
        case 'note-priorities':
          this.companyNotesListsExpanded = true;
          this.$router.push(`/company/${companyId}/dashboard/company/support-lists/note-priorities`);
          break;
      }
    },
    navigateCompanyFixedAssets(type) {
      this.activeMenu = 'company';
      this.companyMenuExpanded = true;
      this.companyFixedAssetsExpanded = true;
      const companyId = this.$route.params.id;
      switch(type) {
        case 'new':
          this.$router.push(`/company/${companyId}/dashboard/company/fixed-assets/new`);
          break;
        case 'depreciation':
          this.$router.push(`/company/${companyId}/dashboard/company/fixed-assets/depreciation`);
          break;
        case 'pending':
          this.$router.push(`/company/${companyId}/dashboard/company/fixed-assets/pending-items`);
          break;
        case 'registered':
          this.$router.push(`/company/${companyId}/dashboard/company/fixed-assets/registered-items`);
          break;
        case 'sold':
          this.$router.push(`/company/${companyId}/dashboard/company/fixed-assets/sold`);
          break;
      }
    },
    navigateCompanyMergeAccounts(type) {
      this.activeMenu = 'company';
      this.companyMenuExpanded = true;
      this.companyMergeAccountsExpanded = true;
      const companyId = this.$route.params.id;
      switch(type) {
        case 'customers':
          this.$router.push(`/company/${companyId}/dashboard/company/merge-accounts/customers`);
          break;
        case 'vendors':
          this.$router.push(`/company/${companyId}/dashboard/company/merge-accounts/vendors`);
          break;
        case 'accounts':
          this.$router.push(`/company/${companyId}/dashboard/company/merge-accounts/accounts`);
          break;
        case 'items':
          this.$router.push(`/company/${companyId}/dashboard/company/merge-accounts/items`);
          break;
      }
    },
    navigateCompanyLists(type) {
      this.activeMenu = 'company';
      this.companyMenuExpanded = true;
      this.companyListsExpanded = true;
      const companyId = this.$route.params.id;
      switch(type) {
        case 'accounts':
          this.$router.push(`/company/${companyId}/dashboard/company-account-list`);
          break;
        case 'items':
          this.$router.push(`/company/${companyId}/dashboard/company/lists/items`);
          break;
        case 'customers':
          this.$router.push(`/company/${companyId}/dashboard/customers/list`);
          break;
        case 'consultants':
          this.$router.push(`/company/${companyId}/dashboard/consultant/list`);
          break;
        case 'vendors':
          this.$router.push(`/company/${companyId}/dashboard/vendors/list`);
          break;
        case 'salespersons':
          this.$router.push(`/company/${companyId}/dashboard/company/lists/salespersons`);
          break;
        case 'activitylog':
          this.$router.push(`/company/${companyId}/dashboard/company/lists/activitylog`);
          break;
        case 'recurring':
          this.$router.push(`/company/${companyId}/dashboard/company/lists/recurring`);
          break;
        case 'reminders':
          this.$router.push(`/company/${companyId}/dashboard/company/lists/reminders`);
          break;
        case 'currency':
          // this.$router.push(`/company/${companyId}/dashboard/company/lists/currency`);
          break;
        case 'importcsv':
          this.$router.push(`/company/${companyId}/dashboard/importcsv`);
          break;
        case 'import-staging':
          this.$router.push(`/company/${companyId}/dashboard/import-staging`);
          break;
      }
    },
    navigateReportsSub(sub) {
      this.activeMenu = 'reports';
      this.reportsMenuExpanded = true;
      // Navigate to Reports Home if requested
      if (sub === 'home') {
        const companyId = this.$route.params.id;
        this.$router.push(`/company/${companyId}/dashboard/reports/home`);
      }
    },
    navigateReportsCompanyFinance(type) {
      this.activeMenu = 'reports';
      this.reportsMenuExpanded = true;
      this.reportsCompanyFinanceExpanded = true;
      const companyId = this.$route.params.id;
      switch(type) {
        case 'profitloss':
          this.$router.push(`/company/${companyId}/dashboard/reports/company-finance/profit-loss`);
          break;
        case 'balancesheet':
          this.$router.push(`/company/${companyId}/dashboard/reports/company-finance/balance-sheet`);
          break;
        case 'cashflow':
          this.$router.push(`/company/${companyId}/dashboard/reports/company-finance/cash-flow`);
          break;
        case 'trialbalance':
          this.$router.push(`/company/${companyId}/dashboard/reports/company-finance/trial-balance`);
          break;
        case 'transactiondetail':
          this.$router.push(`/company/${companyId}/dashboard/reports/company-finance/transaction-detail`);
          break;
        case 'incomebycustomer':
          this.$router.push(`/company/${companyId}/dashboard/reports/company-finance/income-by-customer`);
          break;
        case 'generalledger':
          this.$router.push(`/company/${companyId}/dashboard/reports/company-finance/general-ledger`);
          break;
        case 'expense':
          this.$router.push(`/company/${companyId}/dashboard/reports/company-finance/expense`);
          break;
        case 'automatictransactions':
          this.$router.push(`/company/${companyId}/dashboard/reports/company-finance/automatic-transactions`);
          break;
        case 'reconciliation':
          this.$router.push(`/company/${companyId}/dashboard/reports/company-finance/reconciliation`);
          break;
      }
    },
    navigateReportsCustomersReceivable(type) {
      this.activeMenu = 'reports';
      this.reportsMenuExpanded = true;
      this.reportsCustomersReceivableExpanded = true;
      const companyId = this.$route.params.id;
      switch(type) {
        case 'agingSummary':
          this.$router.push(`/company/${companyId}/dashboard/reports/customers-receivable/ar-aging-summary`);
          break;
        case 'agingDetail':
          this.$router.push(`/company/${companyId}/dashboard/reports/customers-receivable/ar-aging-detail`);
          break;
        case 'statement':
          this.$router.push(`/company/${companyId}/dashboard/reports/customers-receivable/client-statement`);
          break;
        case 'history':
          this.$router.push(`/company/${companyId}/dashboard/reports/customers-receivable/customer-transaction-history`);
          break;
      }
    },
    navigateReportsSales(type) {
      this.activeMenu = 'reports';
      this.reportsMenuExpanded = true;
      this.reportsSalesExpanded = true;
      switch(type) {
        case 'customerSummary':
          this.$router.push(`/company/${this.$route.params.id}/dashboard/reports/sales/customer-summary`);
          break;
        case 'customerDetail':
          this.$router.push(`/company/${this.$route.params.id}/dashboard/reports/sales/customer-detail`);
          break;
        case 'itemSummary':
          this.$router.push(`/company/${this.$route.params.id}/dashboard/reports/sales/item-summary`);
          break;
        case 'itemDetail':
          this.$router.push(`/company/${this.$route.params.id}/dashboard/reports/sales/item-detail`);
          break;
      }
    },
    navigateReportsPurchase(type) {
      this.activeMenu = 'reports';
      this.reportsMenuExpanded = true;
      this.reportsPurchaseExpanded = true;
      const companyId = this.$route.params.id;
      switch(type) {
        case 'vendorSummary':
          this.$router.push(`/company/${companyId}/dashboard/reports/purchase/purchase-by-vendor-summary`);
          break;
        case 'vendorDetail':
          this.$router.push(`/company/${companyId}/dashboard/reports/purchase/purchase-by-vendor-detail`);
          break;
        case 'itemSummary':
          this.$router.push(`/company/${companyId}/dashboard/reports/purchase/purchase-by-item-summary`);
          break;
        case 'itemDetail':
          this.$router.push(`/company/${companyId}/dashboard/reports/purchase/purchase-by-item-detail`);
          break;
      }
    },
    navigateReportsBudget(type) {
      this.activeMenu = 'reports';
      this.reportsMenuExpanded = true;
      this.reportsBudgetExpanded = true;
      const companyId = this.$route.params.id;
      switch(type) {
        case 'overview':
          this.$router.push(`/company/${companyId}/dashboard/reports/budget/overview`);
          break;
        case 'vsactuals':
          this.$router.push(`/company/${companyId}/dashboard/reports/budget/vs-actuals`);
          break;
      }
    },
    navigateToImportCSV() {
      const companyId = this.$route.params.id;
      this.$router.push(`/company/${companyId}/dashboard/importcsv`);
    },
    toggleConsultantMenu() {
      this.consultantMenuExpanded = !this.consultantMenuExpanded;
      this.activeMenu = 'consultant';
    },
    toggleConsultantNewMenu() {
      this.consultantNewMenuExpanded = !this.consultantNewMenuExpanded;
    },
    toggleConsultantPayrollMenu() {
      this.consultantPayrollMenuExpanded = !this.consultantPayrollMenuExpanded;
    },
    toggleConsultantBenefitsMenu() {
      this.consultantBenefitsMenuExpanded = !this.consultantBenefitsMenuExpanded;
    },
    toggleConsultantContractMenu() {
      this.consultantContractMenuExpanded = !this.consultantContractMenuExpanded;
    },
    navigateConsultantSub(sub) {
      this.activeMenu = 'consultant';
      this.consultantMenuExpanded = true;
      switch(sub) {
        case 'center':
          this.$router.push(`/company/${this.$route.params.id}/dashboard/consultant/center`); break;
        case 'list':
          this.$router.push(`/company/${this.$route.params.id}/dashboard/consultant/list`);
          break;
        case 'receive':
          this.$router.push(`/company/${this.$route.params.id}/dashboard/consultant/receive-payment`);
          break;
        case 'timesheet':
          this.$router.push(`/company/${this.$route.params.id}/dashboard/consultant/timesheet`); break;
        case 'timesheetSummary':
          this.$router.push(`/company/${this.$route.params.id}/dashboard/consultant/timesheet-summary`); break;
        case 'invoiceList':
          this.$router.push(`/company/${this.$route.params.id}/dashboard/consultant/invoice`); break;
        case 'contractDetails':
          this.consultantContractMenuExpanded = true;
          this.$router.push(`/company/${this.$route.params.id}/dashboard/consultant/contract-details`); break;
        case 'contractTracking':
          this.consultantContractMenuExpanded = true;
          this.$router.push(`/company/${this.$route.params.id}/dashboard/consultant/contract-tracking`); break;
        case 'benefits':
        case 'benefitCategory':
        case 'benefitTypes':
          this.consultantBenefitsMenuExpanded = true;
          this.$router.push(`/company/${this.$route.params.id}/dashboard/consultant/benefits/types`); break;
        case 'benefitPlans':
          this.consultantBenefitsMenuExpanded = true;
          this.$router.push(`/company/${this.$route.params.id}/dashboard/consultant/benefits/plans`); break;
        case 'payroll':
          this.$router.push(`/company/${this.$route.params.id}/dashboard/consultant/payroll`); break;
        case 'payrollHistory':
          this.consultantPayrollMenuExpanded = true;
          this.$router.push(`/company/${this.$route.params.id}/dashboard/consultant/payroll/history`); break;
        case 'recordPayroll':
          this.consultantPayrollMenuExpanded = true;
          this.$router.push({
            path: `/company/${this.$route.params.id}/dashboard/consultant/payroll/record`,
            state: { recordPayrollFresh: true }
          });
          break;
        case 'createPayroll':
          this.consultantPayrollMenuExpanded = true;
          this.$router.push({
            path: `/company/${this.$route.params.id}/dashboard/consultant/payroll/record/manual`,
            state: { manualEntryFresh: true }
          });
          break;
        case 'stagedPayroll':
          this.consultantPayrollMenuExpanded = true;
          this.$router.push(`/company/${this.$route.params.id}/dashboard/consultant/payroll/staging`); break;
        case 'payrollToConsultants':
          if (this.isInvitedUser) return;
          this.consultantPayrollMenuExpanded = true;
          this.$router.push(`/company/${this.$route.params.id}/dashboard/consultant/payroll/to-consultants`); break;
      }
    },
    navigateConsultantNew(type) {
      this.activeMenu = 'consultant';
      this.consultantMenuExpanded = true;
      this.consultantNewMenuExpanded = true;
      switch(type) {
        case 'newConsultant':
          // Show modal to select consultant type (single vs multiple customers)
          this.showConsultantTypeModal = true;
          break;
      }
    },
    handleConsultantTypeSelect(type) {
      this.showConsultantTypeModal = false;
      const companyId = this.$route.params.id;
      
      if (type === 'single') {
        // Navigate to existing single customer consultant form
        this.$router.push({ name: 'AddCustomerConsultant', params: { id: companyId } });
      } else if (type === 'multiple') {
        // Navigate to new multiple customers consultant form
        this.$router.push({ name: 'AddMultipleCustomerConsultant', params: { id: companyId } });
      }
    },
    closeConsultantTypeModal() {
      this.showConsultantTypeModal = false;
    },
    navigateCustomerNew(type) {
      const companyId = this.$route.params.id;
      this.activeMenu = 'customer';
      this.customerMenuExpanded = true;
      this.customerNewMenuExpanded = true;
      switch(type) {
        case 'newCustomer':
          this.$router.push(`/company/${companyId}/dashboard/customers/new`);
          break;
        case 'newSalesItem':
          this.showItemTypeModal = true;
          break;
        case 'newInvoice':
          this.$router.push(`/company/${companyId}/dashboard/customers/new-invoice`);
          break;
        case 'createInvoice':
          this.$router.push(`/company/${companyId}/dashboard/customers/create-invoice`);
          break;
        case 'newCashSale':
          this.$router.push(`/company/${companyId}/dashboard/customers/new-cash-sale`);
          break;
        case 'newCreditMemo':
          this.$router.push(`/company/${companyId}/dashboard/customers/new-credit-memo`);
          break;
      }
    },
    handleSelectItemTypeOk(selectedType) {
      this.selectedItemType = selectedType;
      this.showItemTypeModal = false;
      const companyId = this.$route.params.id;
      
      // Check if this is from vendor menu (New Purchase Item)
      const isFromVendorMenu = this.activeMenu === 'vendor' && this.vendorMenuExpanded;
      
      if (isFromVendorMenu) {
        // Navigation for vendor purchase items
        switch(selectedType) {
          case 'Service Item':
            this.$router.push({
              name: 'VendorServiceItem',
              params: { id: companyId }
            });
            break;
          case 'Non Inventory Item':
            this.$router.push({
              name: 'VendorProductItem',
              params: { id: companyId }
            });
            break;
          case 'Inventory Item':
            this.$router.push({
              name: 'NewCustomerInventoryItem',
              params: { id: companyId }
            });
            break;
          case 'Inventory Assembly':
            this.$router.push({
              name: 'NewCustomerInventoryAssembly',
              params: { id: companyId }
            });
            break;
          default:
            // Default navigation for other item types
            this.$router.push({
              name: 'VendorServiceItem',
              params: { id: companyId }
            });
        }
      } else {
        // Navigation for customer sales items (existing logic)
        switch(selectedType) {
          case 'Service Item':
            this.$router.push({
              name: 'NewCustomerServiceItem',
              params: { id: companyId }
            });
            break;
          case 'Inventory Item':
            this.$router.push({
              name: 'NewCustomerInventoryItem',
              params: { id: companyId }
            });
            break;
          case 'Non Inventory Item':
            this.$router.push({
              name: 'NewCustomerNonInventoryItem',
              params: { id: companyId }
            });
            break;
          case 'Inventory Assembly':
            this.$router.push({
              name: 'NewCustomerInventoryAssembly',
              params: { id: companyId }
            });
            break;
          case 'Add New Consultant':
            this.$router.push({
              name: 'AddCustomerConsultant',
              params: { id: companyId }
            });
            break;
          default:
            this.$router.push({
              name: 'NewCustomerServiceItem',
              params: { id: companyId }
            });
        }
      }
    },
    handleSelectItemTypeCancel() {
      this.showItemTypeModal = false;
    },
    handleSelectPurchaseItemOk(selectedType) {
      this.selectedPurchaseItemType = selectedType;
      this.showPurchaseItemModal = false;
      const companyId = this.$route.params.id;
      
      // Navigation for vendor purchase items
      switch(selectedType) {
        case 'Service Item':
          this.$router.push({
            name: 'VendorServiceItem',
            params: { id: companyId }
          });
          break;
        case 'Product Item':
          this.$router.push({
            name: 'VendorProductItem',
            params: { id: companyId }
          });
          break;
        default:
          // Default navigation for other item types
          this.$router.push({
            name: 'VendorServiceItem',
            params: { id: companyId }
          });
      }
    },
    handleSelectPurchaseItemCancel() {
      this.showPurchaseItemModal = false;
    },
    handleRecordExpensesOk(selectedType) {
      this.selectedExpenseType = selectedType;
      this.showRecordExpensesModal = false;
      // Add navigation logic for selectedType if needed
    },
    handleRecordExpensesCancel() {
      this.showRecordExpensesModal = false;
    },
    navigateReportsBanking(type) {
      this.activeMenu = 'reports';
      this.reportsMenuExpanded = true;
      this.reportsBankingExpanded = true;
      const companyId = this.$route.params.id;
      switch(type) {
        case 'missingChecks':
          this.$router.push(`/company/${companyId}/dashboard/reports/banking/missing-checks`);
          break;
        case 'reconciliationDiscrepancy':
          this.$router.push(`/company/${companyId}/dashboard/reports/banking/reconciliation-discrepancy`);
          break;
        case 'depositDetail':
          this.$router.push(`/company/${companyId}/dashboard/reports/banking/deposit-detail`);
          break;
        case 'checkDetail':
          this.$router.push(`/company/${companyId}/dashboard/reports/banking/check-detail`);
          break;
      }
    },
    navigateReportsPayroll(type) {
      this.activeMenu = 'reports';
      this.reportsMenuExpanded = true;
      this.reportsPayrollExpanded = true;
      const companyId = this.$route.params.id;
      switch(type) {
        case 'paySlipSummary':
          this.$router.push(`/company/${companyId}/dashboard/reports/payroll/pay-slip-summary`);
          break;
        case 'paySlipDetail':
          this.$router.push(`/company/${companyId}/dashboard/reports/payroll/pay-slip-detail`);
          break;
        case 'paySheet':
          this.$router.push(`/company/${companyId}/dashboard/reports/payroll/pay-sheet`);
          break;
        case 'payHeadSummary':
          this.$router.push(`/company/${companyId}/dashboard/reports/payroll/pay-head-summary`);
          break;
        case 'payHeadDetail':
          this.$router.push(`/company/${companyId}/dashboard/reports/payroll/pay-head-detail`);
          break;
        case 'taxSummary':
          this.$router.push(`/company/${companyId}/dashboard/reports/payroll/tax-summary-report`);
          break;
      }
    },
    navigateReportsVendorsPayables(type) {
      this.activeMenu = 'reports';
      this.reportsMenuExpanded = true;
      this.reportsVendorsPayablesExpanded = true;
      const companyId = this.$route.params.id;
      switch(type) {
        case 'agingSummary':
          this.$router.push(`/company/${companyId}/dashboard/reports/vendors-payables/ap-aging-summary`);
          break;
        case 'agingDetail':
          this.$router.push(`/company/${companyId}/dashboard/reports/vendors-payables/ap-aging-detail`);
          break;
        case 'statement':
          this.$router.push(`/company/${companyId}/dashboard/reports/vendors-payables/vendors-statement`);
          break;
        case 'history':
          this.$router.push(`/company/${companyId}/dashboard/reports/vendors-payables/vendor-transaction-history`);
          break;
      }
    },
    navigateReportsFixedAsset(type) {
      this.activeMenu = 'reports';
      this.reportsMenuExpanded = true;
      this.reportsFixedAssetExpanded = true;
      const companyId = this.$route.params.id;
      switch(type) {
        case 'depreciation':
          this.$router.push(`/company/${companyId}/dashboard/reports/fixed-asset/depreciation`);
          break;
      }
    },
    navigateReportsInventory(type) {
      this.activeMenu = 'reports';
      this.reportsMenuExpanded = true;
      this.reportsInventoryExpanded = true;
      const companyId = this.$route.params.id;
      switch(type) {
        case 'valuationSummary':
          this.$router.push(`/company/${companyId}/dashboard/reports/inventory/inventory-valuation-summary`);
          break;
        case 'valuationDetails':
          this.$router.push(`/company/${companyId}/dashboard/reports/inventory/inventory-valuation-details`);
          break;
        case 'stockStatusByItem':
          this.$router.push(`/company/${companyId}/dashboard/reports/inventory/inventory-stock-status-by-item`);
          break;
        case 'details':
          this.$router.push(`/company/${companyId}/dashboard/reports/inventory/inventory-details`);
          break;
      }
    },
    
    // Mobile menu methods
    checkMobileView() {
      this.isMobile = window.innerWidth <= 768;
      if (!this.isMobile) {
        this.mobileMenuOpen = false;
      }
    },
    
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
    },
    
    closeMobileMenu() {
      this.mobileMenuOpen = false;
    },
    
    handleImageError(event) {
      logger.error('Logo image failed to load:', event.target.src);
      // You can add fallback logic here if needed
    },
    handleUserMenuItemClick(item) {
      switch(item.action) {
        case 'settings': {
          // Navigate to personal settings
          const companyId = this.$route.params.id;
          this.$router.push(`/company/${companyId}/dashboard/settings/users`);
          break;
        }
        case 'logout':
          this.handleLogout();
          break;
        default:
          logger.log('Unknown menu item action:', item.action);
      }
    },
    applyUIMode() {
      uiPreferencesService.applyCached();
    },
    handleUIModeChange() {
      // Re-apply mode when changed from UI Preferences component
      this.applyUIMode();
    },
    loadSidebarWidth() {
      try {
        const savedWidth = localStorage.getItem('sidebar_width');
        if (savedWidth) {
          const width = parseInt(savedWidth, 10);
          if (width >= 250 && width <= 600) {
            this.sidebarWidth = width;
          }
        }
      } catch (error) {
        logger.error('Error loading sidebar width:', error);
      }
    },
    saveSidebarWidth() {
      try {
        localStorage.setItem('sidebar_width', this.sidebarWidth.toString());
      } catch (error) {
        logger.error('Error saving sidebar width:', error);
      }
    },
    startResize(e) {
      if (this.sidebarCollapsed || this.isMobile) return;
      
      this.isResizing = true;
      this.resizeStartX = e.clientX;
      this.resizeStartWidth = this.sidebarWidth;
      
      document.addEventListener('mousemove', this.handleResize);
      document.addEventListener('mouseup', this.stopResize);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      
      e.preventDefault();
    },
    handleResize(e) {
      if (!this.isResizing) return;
      
      const diff = e.clientX - this.resizeStartX;
      const newWidth = this.resizeStartWidth + diff;
      
      // Constrain width between 250px and 600px
      const minWidth = 250;
      const maxWidth = 600;
      
      if (newWidth >= minWidth && newWidth <= maxWidth) {
        this.sidebarWidth = newWidth;
      } else if (newWidth < minWidth) {
        this.sidebarWidth = minWidth;
      } else if (newWidth > maxWidth) {
        this.sidebarWidth = maxWidth;
      }
    },
    stopResize() {
      if (!this.isResizing) return;
      
      this.isResizing = false;
      this.saveSidebarWidth();
      
      document.removeEventListener('mousemove', this.handleResize);
      document.removeEventListener('mouseup', this.stopResize);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    },
    loadMascotPanelWidth() {
      try {
        const saved = localStorage.getItem('mascot_panel_width');
        if (!saved) {
          return;
        }
        const width = parseInt(saved, 10);
        if (width >= 280 && width <= 680) {
          this.mascotPanelWidth = width;
        }
      } catch (error) {
        logger.error('Error loading mascot panel width:', error);
      }
    },
    saveMascotPanelWidth() {
      try {
        localStorage.setItem('mascot_panel_width', String(this.mascotPanelWidth));
      } catch (error) {
        logger.error('Error saving mascot panel width:', error);
      }
    },
    startMascotResize(e) {
      if (window.innerWidth <= 900) {
        return;
      }
      this.mascotResizing = true;
      this.mascotResizeStartX = e.clientX;
      this.mascotResizeStartWidth = this.mascotPanelWidth;
      document.addEventListener('mousemove', this.handleMascotResize);
      document.addEventListener('mouseup', this.stopMascotResize);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    handleMascotResize(e) {
      if (!this.mascotResizing) {
        return;
      }
      const delta = this.mascotResizeStartX - e.clientX;
      const next = this.mascotResizeStartWidth + delta;
      this.mascotPanelWidth = Math.max(280, Math.min(680, next));
    },
    stopMascotResize() {
      if (!this.mascotResizing) {
        return;
      }
      this.mascotResizing = false;
      this.saveMascotPanelWidth();
      document.removeEventListener('mousemove', this.handleMascotResize);
      document.removeEventListener('mouseup', this.stopMascotResize);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    },
  },
}
</script>